// ABOUTME: Manages company WhatsApp settings, templates, and connection state.
// ABOUTME: Presents template editing, preview, and setup controls for administrators.
import React from "react";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Tab from "@material-ui/core/Tab";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tabs from "@material-ui/core/Tabs";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import { Alert } from "@material-ui/lab";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import VisibilityIcon from "@material-ui/icons/Visibility";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import Page from "Pages/_Layouts/DefaultInternal";
import { useAuthorization, useAuthState } from "_ReactContext/AuthContext";
import { apiDirectCall, useApiCache } from "customHooks/useApi";
import useShowMessage from "customHooks/useShowMessage/useShowMessage";
import { transformDBData } from "functions/util";
import { getWhatsappApiResponseData } from "functions/whatsappApiResponse";
import { useTranslation } from "react-i18next";
import {
    consumeStoredWhatsappOAuthCallback,
    consumeStoredWhatsappOAuthError,
    formatWhatsappOAuthErrorMessage,
    getWhatsappOAuthErrorFromSearch,
    parseWhatsappOAuthCallbackFromSearch
} from "./whatsappOAuthCallback";
import WhatsappPreRegistrationSettings from "./WhatsappPreRegistrationSettings";
import {
    applyWhatsappPreRegistrationRolloutChange,
    canShowWhatsappPreRegistrationSettings
} from "./whatsappPreRegistrationSettingsModel";
import { updateWhatsappPreRegistrationRollout } from "./whatsappPreRegistrationSettingsApi";
import { WhatsappTemplatePreview } from "./WhatsappTemplatePreview";

const WHATSAPP_MARKETING_OPT_OUT_FOOTER = "Responda PARAR para cancelar.";
const WHATSAPP_MARKETING_OPT_OUT_BUTTON_TEXT = "Parar mensagens";

const getApiErrorMessage = (error: any, fallback: string) => {
    return (
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        fallback
    );
};

const getPlaceholderIndexesFromText = (text: string) => {
    const indexes = new Set<number>();
    const matches = String(text || "").matchAll(/\{\{(\d+)\}\}/g);

    for (const match of matches) {
        indexes.add(Number(match[1]));
    }

    return [...indexes].sort((left, right) => left - right);
};

const getTemplateBodyText = (template: Compleo.IObject | null | undefined) => {
    const bodyComponent = (
        (template?.components || []) as Compleo.IObject[]
    ).find(
        (component) => String(component.type || "").toUpperCase() === "BODY"
    );

    return String(bodyComponent?.text || template?.bodyText || "");
};

const getTemplateParameterExamples = (
    template: Compleo.IObject | null | undefined
) => {
    const bodyComponent = (
        (template?.components || []) as Compleo.IObject[]
    ).find(
        (component) => String(component.type || "").toUpperCase() === "BODY"
    );
    const bodyTextExamples = bodyComponent?.example?.body_text;

    if (Array.isArray(bodyTextExamples?.[0])) {
        return bodyTextExamples[0].map((item: unknown) => String(item || ""));
    }

    if (Array.isArray(template?.parameterExamples)) {
        return template.parameterExamples.map((item: unknown) =>
            String(item || "")
        );
    }

    return [];
};

const buildParameterDefinitions = (params: {
    bodyText: string;
    previousDefinitions?: Compleo.IObject[];
    fallbackLabels?: string[];
    fallbackExamples?: string[];
    componentType?: "header" | "body";
    messageType?: string;
}) => {
    const previousByIndex = new Map<string, Compleo.IObject>();

    for (const definition of params.previousDefinitions || []) {
        previousByIndex.set(
            `${definition.componentType || params.componentType || "body"}:${definition.index || ""}`,
            definition
        );
    }

    return getPlaceholderIndexesFromText(params.bodyText).map((index) => {
        const componentType = params.componentType || "body";
        const previous = previousByIndex.get(`${componentType}:${index}`);
        const defaultCatalogItem = params.messageType
            ? getDefaultParameterCatalogItem(params.messageType, index)
            : null;
        const sourceKey = previous?.sourceKey || defaultCatalogItem?.key || "";
        const catalogItem =
            params.messageType && sourceKey
                ? getParameterCatalogItem(params.messageType, sourceKey)
                : null;
        const sourceType =
            previous?.sourceType ||
            catalogItem?.sourceType ||
            (sourceKey ? "SYSTEM" : "");

        return {
            index,
            componentType,
            label: String(
                previous?.label ||
                    catalogItem?.label ||
                    params.fallbackLabels?.[index - 1] ||
                    `Parametro ${index}`
            ),
            example: String(
                previous?.example ||
                    catalogItem?.example ||
                    params.fallbackExamples?.[index - 1] ||
                    ""
            ),
            sourceKey,
            sourceType
        };
    });
};

const getNextRevisionName = (
    template: Compleo.IObject,
    companyTemplates: Compleo.IObject[]
) => {
    const currentName = String(template.templateName || template.name || "");
    const baseName = currentName.replace(/_v\d+$/i, "");
    const usedNames = new Set(
        companyTemplates.map((item) =>
            String(item.templateName || item.name || "").toLowerCase()
        )
    );

    for (let version = 2; version < 100; version += 1) {
        const candidate = `${baseName}_v${version}`;
        if (!usedNames.has(candidate.toLowerCase())) {
            return candidate;
        }
    }

    return `${baseName}_v${Date.now()}`;
};

const getTemplateParameterDefinitionsForPreview = (
    template: Compleo.IObject | null | undefined
) =>
    buildParameterDefinitions({
        bodyText: getTemplateBodyText(template),
        previousDefinitions: template?.parameterDefinitions || [],
        fallbackLabels: template?.parameterLabels || [],
        fallbackExamples: getTemplateParameterExamples(template)
    });

const WHATSAPP_TEMPLATE_MESSAGE_TYPES = [
    {
        key: "recruiting_process",
        label: "Processo seletivo",
        parameters: [
            {
                key: "applicant.firstName",
                label: "Primeiro nome",
                example: "Maria"
            },
            {
                key: "applicant.name",
                label: "Nome completo",
                example: "Maria Silva"
            },
            {
                key: "job.title",
                label: "Titulo da vaga",
                example: "Analista de RH"
            },
            {
                key: "job.description",
                label: "Descricao da vaga",
                example: "Atuacao no time de RH"
            },
            {
                key: "job.customerName",
                label: "Departamento",
                example: "Compleo"
            },
            {
                key: "company.name",
                label: "Nome da empresa",
                example: "Compleo"
            },
            {
                key: "user.email",
                label: "E-mail do recrutador",
                example: "recrutador@empresa.com"
            }
        ]
    },
    {
        key: "interview",
        label: "Entrevista",
        parameters: [
            {
                key: "applicant.firstName",
                label: "Primeiro nome",
                example: "Maria"
            },
            {
                key: "job.title",
                label: "Titulo da vaga",
                example: "Analista de RH"
            },
            {
                key: "schedule.startDateTimeFormatted",
                label: "Data e horario",
                example: "25/04 as 10h"
            },
            {
                key: "schedule.locationOrLink",
                label: "Local ou link",
                example: "https://meet.google.com/abc"
            }
        ]
    },
    {
        key: "assessment",
        label: "Teste ou avaliacao",
        parameters: [
            {
                key: "applicant.firstName",
                label: "Primeiro nome",
                example: "Maria"
            },
            {
                key: "job.title",
                label: "Titulo da vaga",
                example: "Analista de RH"
            },
            {
                key: "assessment.link",
                label: "Link da etapa",
                example: "https://ats.compleo.com.br/teste"
            },
            {
                key: "assessment.deadlineFormatted",
                label: "Prazo",
                example: "30/04"
            }
        ]
    },
    {
        key: "new_opportunity",
        label: "Nova oportunidade",
        parameters: [
            {
                key: "applicant.firstName",
                label: "Primeiro nome",
                example: "Maria"
            },
            {
                key: "job.title",
                label: "Titulo da vaga",
                example: "Analista de RH"
            },
            {
                key: "job.link",
                label: "Link da vaga",
                example: "https://empresa.compleo.com.br/vagas"
            }
        ]
    },
    {
        key: "custom",
        label: "Mensagem livre",
        parameters: [
            {
                key: "applicant.firstName",
                label: "Primeiro nome",
                example: "Maria"
            },
            {
                key: "applicant.name",
                label: "Nome completo",
                example: "Maria Silva"
            },
            {
                key: "company.name",
                label: "Nome da empresa",
                example: "Compleo"
            },
            {
                key: "user.email",
                label: "E-mail do recrutador",
                example: "recrutador@empresa.com"
            }
        ]
    }
];

const getWhatsappTemplateCatalog = (messageType: string) =>
    WHATSAPP_TEMPLATE_MESSAGE_TYPES.find((item) => item.key === messageType) ||
    WHATSAPP_TEMPLATE_MESSAGE_TYPES[0];

const getWhatsappTemplateParameterCatalog = (messageType: string) =>
    getWhatsappTemplateCatalog(messageType).parameters;

const getParameterCatalogItem = (messageType: string, sourceKey: string) =>
    getWhatsappTemplateParameterCatalog(messageType).find(
        (item) => item.key === sourceKey
    );

const getDefaultParameterCatalogItem = (messageType: string, index: number) => {
    const parameters = getWhatsappTemplateParameterCatalog(messageType);
    return parameters[index - 1] || parameters[0];
};

const createWhatsappActionButtonStyle = (
    color: string,
    disabled = false
): React.CSSProperties => ({
    color: disabled ? "#9e9e9e" : color,
    backgroundColor: disabled ? "#00000008" : `${color}18`,
    cursor: disabled ? "not-allowed" : "pointer",
    marginLeft: 4
});

const getTemplateLastUpdateValue = (template: Compleo.IObject) =>
    template.lastMetaWebhookAt ||
    template.lastMetaSyncAt ||
    template.updatedAt ||
    "";

const getLabels = (language: string) => {
    const isPortuguese = (language || "").toLowerCase().startsWith("pt");

    if (isPortuguese) {
        return {
            featureName: "Configurações de WhatsApp",
            featureDescription:
                "Conecte a conta oficial da empresa, sincronize templates aprovados e libere o envio para recrutadores.",
            statusTitle: "Status da integração",
            connected: "Conectado",
            notConnected: "Nenhuma conexão ativa foi encontrada.",
            featureEnabled: "Recurso habilitado para a empresa",
            featureDisabled: "Recurso desabilitado para a empresa",
            disabledWithStoredConnection:
                "A empresa possui uma conexão armazenada, mas o recurso está desabilitado. Webhooks e envios permanecem bloqueados.",
            enable: "Habilitar WhatsApp",
            disable: "Desabilitar WhatsApp",
            refreshStatus: "Atualizar status",
            generateAuthUrl: "Gerar URL de autorização",
            syncTemplates: "Sincronizar templates",
            connect: "Concluir conexão",
            authCode: "Authorization code",
            oauthState: "State da autorização",
            generatedAuthUrl: "URL gerada",
            openGeneratedAuthUrl: "Continuar na Meta",
            authHelp:
                "Depois de autorizar na Meta, o retorno será processado automaticamente nesta tela. Se necessário, confirme manualmente o authorization code e o state.",
            enableBeforeConnect:
                "Habilite o WhatsApp para a empresa antes de gerar a URL de autorização.",
            generateSuccess:
                "URL de autorização gerada. Use o retorno da Meta para concluir a conexão.",
            connectSuccess: "Conta do WhatsApp Business conectada com sucesso.",
            connectWebhookWarning:
                "Conta conectada, mas a assinatura dos webhooks da WABA falhou. Use a opção de retry antes de testar recebimento de mensagens.",
            webhookSubscriptionTitle: "Webhooks da WABA",
            webhookSubscriptionSubscribed:
                "Assinatura ativa para mensagens e atualizações de templates.",
            webhookSubscriptionUnverified:
                "A Meta aceitou a assinatura, mas o Compleo ainda não conseguiu confirmar o app na consulta de verificação.",
            webhookSubscriptionFailed:
                "A WABA ainda não está assinada no app da Meta. Mensagens recebidas podem não chegar ao Compleo.",
            webhookSubscriptionUnknown:
                "Ainda não foi possível confirmar a assinatura dos webhooks da WABA.",
            resubscribeWebhooks: "Assinar webhooks",
            resubscribingWebhooks: "Assinando...",
            resubscribeWebhooksSuccess:
                "Assinatura dos webhooks da WABA atualizada.",
            resubscribeWebhooksFailure:
                "Não foi possível assinar os webhooks da WABA. Verifique as permissões na Meta e tente novamente.",
            selectionRequired:
                "Selecione a opção correta da Meta para continuar a conexão.",
            continueSelection: "Continuar conexão",
            selectMetaBusiness: "Selecione o Business da Meta",
            selectMetaWaba: "Selecione a conta WhatsApp Business",
            selectMetaPhone: "Selecione o número de telefone",
            invalidSelection: "Selecione uma opção para continuar.",
            phoneNumbersTitle: "Número do WhatsApp",
            phoneNumbersDescription:
                "Escolha qual número desta conta WhatsApp Business o Compleo deve usar para enviar e receber mensagens.",
            phoneNumberField: "Número ativo no Compleo",
            phoneNumberStatus: "Status do número",
            phoneNumberQuality: "Qualidade",
            phoneNumberReady: "Pronto para envio e recebimento",
            phoneNumberPending:
                "Este número ainda não está conectado para uso na Cloud API. Informe o PIN de 6 dígitos para ativá-lo.",
            phoneNumberUnknown:
                "A Meta não retornou o status detalhado deste número. Atualize o status antes de enviar mensagens.",
            phoneNumberSelected:
                "Número do WhatsApp atualizado para esta empresa.",
            phoneNumberRegistered:
                "Número registrado na Meta. Atualize o status se a Meta ainda não retornar conectado.",
            phoneNumberSelectAction: "Usar este número",
            registerPhoneNumber: "Ativar número",
            registrationPin: "PIN de 6 dígitos",
            registrationPinHelp:
                "Este PIN é a verificação em duas etapas do número do WhatsApp Business. Ele não é o código recebido por SMS ou ligação.",
            registrationPinCreation:
                "Se o número ainda não tiver PIN, o valor informado aqui será criado como o novo PIN na Meta. Se já tiver PIN, informe o PIN existente ou redefina-o no WhatsApp Manager.",
            registrationPinStorage:
                "O Compleo não armazena este PIN; ele é enviado apenas para a Meta durante a ativação do número.",
            registrationPinAttemptLimit:
                "Evite várias tentativas seguidas. A Meta limita chamadas de ativação do número em uma janela de 72 horas.",
            invalidRegistrationPin:
                "Informe o PIN de 6 dígitos configurado para o número na Meta.",
            syncSuccess: "Templates sincronizados com sucesso.",
            toggleEnabledSuccess: "Flag do WhatsApp atualizada com sucesso.",
            invalidConnect:
                "Preencha o authorization code e o state antes de concluir a conexão.",
            oauthDenied:
                "A Meta recusou ou cancelou a autorização do WhatsApp. Gere uma nova URL e tente novamente.",
            oauthMetaUnavailable:
                "O Login do Facebook está indisponível para o app CompleoNext na Meta. Peça ao time técnico para concluir as pendências descritas no runbook de OAuth WhatsApp.",
            connectIncomplete:
                "A Meta retornou a autorização, mas a conexão ainda não foi marcada como ativa.",
            templatesTitle: "Templates sugeridos",
            templatesDescription:
                "Copie um modelo inicial para a conta WhatsApp Business da empresa. O envio fica disponível apenas depois da aprovação da Meta e da sincronização.",
            templateField: "Modelo sugerido",
            templateName: "Nome técnico do template",
            templateBody: "Texto do template",
            templateCreate: "Criar template na Meta",
            templateCreated:
                "Template criado na Meta. Sincronize depois da aprovação para liberar o envio.",
            templateCreationMode: "Modo de criação",
            suggestedTemplateMode: "Usar modelo sugerido",
            customTemplateMode: "Criar do zero",
            customTemplateTitle: "Template personalizado",
            customTemplateDescription:
                "Crie um template livre para aprovação da Meta usando parâmetros controlados pelo Compleo.",
            messageType: "Tipo de mensagem",
            headerText: "Header",
            footerText: "Footer",
            buttons: "Botões",
            addButton: "Adicionar botão",
            removeButton: "Remover botão",
            buttonType: "Tipo do botão",
            buttonText: "Texto do botão",
            buttonUrl: "URL",
            buttonPhone: "Telefone",
            quickReply: "Resposta rápida",
            urlButton: "Abrir URL",
            phoneButton: "Ligar",
            parameterSource: "Campo do Compleo",
            metaReviewTime:
                "A análise da Meta normalmente acontece em minutos ou até 24h, mas pode demorar mais em contas novas, templates promocionais, links ou revisão manual.",
            templateStatus: "Status na empresa",
            templateNotCreated: "Ainda não criado",
            templateParameters: "Parâmetros",
            templateCategory: "Categoria",
            templateRequestedCategory: "Categoria solicitada",
            templateCurrentCategory: "Categoria atual na Meta",
            templatePendingCategory: "Próxima categoria sinalizada",
            requiredTemplate: "Selecione um template sugerido.",
            configurationTab: "Configuração",
            templatesTab: "Templates",
            preRegistrationTab: "Pré-cadastro",
            companyTemplatesTitle: "Templates da empresa",
            companyTemplatesDescription:
                "Acompanhe os templates enviados para a Meta, o status de aprovação, a categoria aprovada e a qualidade retornada pela Meta.",
            noCompanyTemplates:
                "Nenhum template WhatsApp foi encontrado para esta empresa.",
            language: "Idioma",
            quality: "Qualidade",
            origin: "Origem",
            lastUpdate: "Última atualização",
            actions: "Ações",
            viewDetails: "Ver detalhes",
            createRevision: "Criar nova versão",
            activateForSending: "Usar no envio",
            activeForSending: "Ativo para envio",
            activateTemplateSuccess: "Template marcado como ativo para envio.",
            revisionInfo:
                "Você está criando uma nova versão. O template atual continua disponível enquanto a Meta analisa a nova submissão.",
            revisionSource: "Template de origem",
            revisionPrepared:
                "Nova versão preparada a partir do template {{templateName}}. Revise os campos e envie para a Meta.",
            cannotReviseTemplate:
                "Só é possível criar nova versão para templates copiados dos modelos sugeridos.",
            filterStatus: "Filtrar status",
            allStatuses: "Todos os status",
            templateDetails: "Detalhes do template",
            previewTitle: "Preview no WhatsApp",
            parametersHelp:
                "Informe um rótulo e um exemplo real para cada variável. A Meta usa os exemplos para avaliar o template; o Compleo usa os rótulos para orientar o envio.",
            parameterLabel: "Rótulo",
            parameterExample: "Exemplo",
            requiredParameters:
                "Preencha rótulo e exemplo de todos os parâmetros.",
            invalidParameterSequence:
                "Use parâmetros sequenciais começando em {{1}}.",
            copiedFrom: "Copiado de",
            close: "Fechar"
        };
    }

    return {
        featureName: "WhatsApp Settings",
        featureDescription:
            "Connect the company official account, sync approved templates, and enable recruiter usage.",
        statusTitle: "Integration status",
        connected: "Connected",
        notConnected: "No active connection was found.",
        featureEnabled: "Feature enabled for the company",
        featureDisabled: "Feature disabled for the company",
        disabledWithStoredConnection:
            "The company has a stored connection, but the feature is disabled. Webhooks and sends remain blocked.",
        enable: "Enable WhatsApp",
        disable: "Disable WhatsApp",
        refreshStatus: "Refresh status",
        generateAuthUrl: "Generate auth URL",
        syncTemplates: "Sync templates",
        connect: "Finish connection",
        authCode: "Authorization code",
        oauthState: "OAuth state",
        generatedAuthUrl: "Generated URL",
        openGeneratedAuthUrl: "Continue in Meta",
        authHelp:
            "After authorizing in Meta, the callback will be processed automatically on this page. If needed, confirm the authorization code and state manually.",
        enableBeforeConnect:
            "Enable WhatsApp for the company before generating the authorization URL.",
        generateSuccess:
            "Authorization URL generated. Use the Meta callback values to finish the connection.",
        connectSuccess: "WhatsApp Business account connected successfully.",
        connectWebhookWarning:
            "Account connected, but WABA webhook subscription failed. Retry before testing inbound messages.",
        webhookSubscriptionTitle: "WABA webhooks",
        webhookSubscriptionSubscribed:
            "Active subscription for messages and template updates.",
        webhookSubscriptionUnverified:
            "Meta accepted the subscription, but Compleo could not confirm the app in the verification query yet.",
        webhookSubscriptionFailed:
            "This WABA is not subscribed to the Meta app yet. Inbound messages may not reach Compleo.",
        webhookSubscriptionUnknown:
            "Compleo could not confirm the WABA webhook subscription yet.",
        resubscribeWebhooks: "Subscribe webhooks",
        resubscribingWebhooks: "Subscribing...",
        resubscribeWebhooksSuccess: "WABA webhook subscription updated.",
        resubscribeWebhooksFailure:
            "Could not subscribe WABA webhooks. Check Meta permissions and try again.",
        selectionRequired:
            "Select the correct Meta option to continue the connection.",
        continueSelection: "Continue connection",
        selectMetaBusiness: "Select the Meta Business",
        selectMetaWaba: "Select the WhatsApp Business Account",
        selectMetaPhone: "Select the phone number",
        invalidSelection: "Select an option to continue.",
        phoneNumbersTitle: "WhatsApp phone number",
        phoneNumbersDescription:
            "Choose which phone number from this WhatsApp Business Account Compleo should use to send and receive messages.",
        phoneNumberField: "Active phone number in Compleo",
        phoneNumberStatus: "Phone number status",
        phoneNumberQuality: "Quality",
        phoneNumberReady: "Ready for sending and receiving",
        phoneNumberPending:
            "This phone number is not connected for Cloud API usage yet. Enter the 6-digit PIN to activate it.",
        phoneNumberUnknown:
            "Meta did not return detailed status for this phone number. Refresh status before sending messages.",
        phoneNumberSelected: "WhatsApp phone number updated for this company.",
        phoneNumberRegistered:
            "Phone number registered in Meta. Refresh status if Meta does not return connected yet.",
        phoneNumberSelectAction: "Use this number",
        registerPhoneNumber: "Activate phone number",
        registrationPin: "6-digit PIN",
        registrationPinHelp:
            "This PIN is the two-step verification PIN for the WhatsApp Business phone number. It is not the SMS or voice verification code.",
        registrationPinCreation:
            "If the phone number does not have a PIN yet, the value entered here will become the new PIN in Meta. If it already has one, enter the existing PIN or reset it in WhatsApp Manager.",
        registrationPinStorage:
            "Compleo does not store this PIN; it is sent only to Meta while activating the phone number.",
        registrationPinAttemptLimit:
            "Avoid repeated attempts. Meta limits phone number activation calls within a 72-hour window.",
        invalidRegistrationPin:
            "Enter the 6-digit PIN configured for this phone number in Meta.",
        syncSuccess: "Templates synced successfully.",
        toggleEnabledSuccess: "WhatsApp flag updated successfully.",
        invalidConnect:
            "Enter the authorization code and state before finishing the connection.",
        oauthDenied:
            "Meta denied or cancelled the WhatsApp authorization. Generate a new URL and try again.",
        oauthMetaUnavailable:
            "Facebook Login is unavailable for the CompleoNext Meta app. Ask the technical team to complete the pending items in the WhatsApp OAuth runbook.",
        connectIncomplete:
            "Meta returned the authorization, but the connection was not marked active yet.",
        templatesTitle: "Suggested templates",
        templatesDescription:
            "Copy a starter template into the company's WhatsApp Business account. Sending becomes available only after Meta approval and sync.",
        templateField: "Suggested template",
        templateName: "Technical template name",
        templateBody: "Template text",
        templateCreate: "Create template in Meta",
        templateCreated:
            "Template created in Meta. Sync after approval to enable sending.",
        templateCreationMode: "Creation mode",
        suggestedTemplateMode: "Use suggested template",
        customTemplateMode: "Create from scratch",
        customTemplateTitle: "Custom template",
        customTemplateDescription:
            "Create a free-form template for Meta approval using Compleo-controlled parameters.",
        messageType: "Message type",
        headerText: "Header",
        footerText: "Footer",
        buttons: "Buttons",
        addButton: "Add button",
        removeButton: "Remove button",
        buttonType: "Button type",
        buttonText: "Button text",
        buttonUrl: "URL",
        buttonPhone: "Phone",
        quickReply: "Quick reply",
        urlButton: "Open URL",
        phoneButton: "Call",
        parameterSource: "Compleo field",
        metaReviewTime:
            "Meta review usually happens within minutes or up to 24h, but it may take longer for new accounts, promotional templates, links, or manual review.",
        templateStatus: "Company status",
        templateNotCreated: "Not created yet",
        templateParameters: "Parameters",
        templateCategory: "Category",
        templateRequestedCategory: "Requested category",
        templateCurrentCategory: "Current Meta category",
        templatePendingCategory: "Pending category signaled",
        requiredTemplate: "Select a suggested template.",
        configurationTab: "Configuration",
        templatesTab: "Templates",
        preRegistrationTab: "Pre-registration",
        companyTemplatesTitle: "Company templates",
        companyTemplatesDescription:
            "Track templates sent to Meta, approval status, approved category, and quality returned by Meta.",
        noCompanyTemplates: "No WhatsApp template was found for this company.",
        language: "Language",
        quality: "Quality",
        origin: "Origin",
        lastUpdate: "Last update",
        actions: "Actions",
        viewDetails: "View details",
        createRevision: "Create new version",
        activateForSending: "Use for sending",
        activeForSending: "Active for sending",
        activateTemplateSuccess: "Template marked as active for sending.",
        revisionInfo:
            "You are creating a new version. The current template remains available while Meta reviews the new submission.",
        revisionSource: "Source template",
        revisionPrepared:
            "New version prepared from template {{templateName}}. Review the fields and submit it to Meta.",
        cannotReviseTemplate:
            "Only templates copied from suggested templates can be versioned here.",
        filterStatus: "Filter status",
        allStatuses: "All statuses",
        templateDetails: "Template details",
        previewTitle: "WhatsApp preview",
        parametersHelp:
            "Enter a label and a realistic example for each variable. Meta uses examples to review the template; Compleo uses labels to guide sending.",
        parameterLabel: "Label",
        parameterExample: "Example",
        requiredParameters: "Fill label and example for every parameter.",
        invalidParameterSequence:
            "Use sequential parameters starting at {{1}}.",
        copiedFrom: "Copied from",
        close: "Close"
    };
};

const WhatsappSettings = () => {
    const { company, reloadCompanyAndPermissionData } = useAuthState();
    const { isFeatureAuthorized } = useAuthorization();
    const showMessage = useShowMessage();
    const [t, i18n] = useTranslation(undefined, { useSuspense: false });
    const labels = React.useMemo(
        () => getLabels(i18n.language),
        [i18n.language]
    );

    const [isTogglingWhatsappEnabled, setIsTogglingWhatsappEnabled] =
        React.useState(false);
    const [isTogglingPreRegistrationRollout, setIsTogglingPreRegistrationRollout] =
        React.useState(false);
    const [isGeneratingWhatsappUrl, setIsGeneratingWhatsappUrl] =
        React.useState(false);
    const [isConnectingWhatsapp, setIsConnectingWhatsapp] =
        React.useState(false);
    const [isSyncingWhatsappTemplates, setIsSyncingWhatsappTemplates] =
        React.useState(false);
    const [isCreatingWhatsappTemplate, setIsCreatingWhatsappTemplate] =
        React.useState(false);
    const [isSelectingWhatsappPhoneNumber, setIsSelectingWhatsappPhoneNumber] =
        React.useState(false);
    const [
        isRegisteringWhatsappPhoneNumber,
        setIsRegisteringWhatsappPhoneNumber
    ] = React.useState(false);
    const [isSubscribingWabaWebhooks, setIsSubscribingWabaWebhooks] =
        React.useState(false);
    const [isActivatingTemplateName, setIsActivatingTemplateName] =
        React.useState("");
    const [oauthState, setOauthState] = React.useState("");
    const [authCode, setAuthCode] = React.useState("");
    const [generatedAuthUrl, setGeneratedAuthUrl] = React.useState("");
    const [selectedPhoneNumberId, setSelectedPhoneNumberId] =
        React.useState("");
    const [registrationPin, setRegistrationPin] = React.useState("");
    const [selectionSessionId, setSelectionSessionId] = React.useState("");
    const [selectionStep, setSelectionStep] = React.useState("");
    const [selectionOptions, setSelectionOptions] = React.useState<
        Compleo.IObject[]
    >([]);
    const [selectedMetaOptionId, setSelectedMetaOptionId] = React.useState("");
    const [pendingOAuthCallback, setPendingOAuthCallback] =
        React.useState<Compleo.IObject | null>(null);
    const [connectionOverride, setConnectionOverride] =
        React.useState<Compleo.IObject | null>(null);
    const [selectedGlobalTemplateKey, setSelectedGlobalTemplateKey] =
        React.useState("");
    const [templateCreationMode, setTemplateCreationMode] =
        React.useState("SUGGESTED");
    const [customTemplateMessageType, setCustomTemplateMessageType] =
        React.useState("recruiting_process");
    const [customTemplateCategory, setCustomTemplateCategory] =
        React.useState("UTILITY");
    const [customTemplateLanguage, setCustomTemplateLanguage] =
        React.useState("pt_BR");
    const [customTemplateHeader, setCustomTemplateHeader] = React.useState("");
    const [customTemplateFooter, setCustomTemplateFooter] = React.useState("");
    const [customTemplateButtons, setCustomTemplateButtons] = React.useState<
        Compleo.IObject[]
    >([]);
    const [globalTemplateName, setGlobalTemplateName] = React.useState("");
    const [globalTemplateBody, setGlobalTemplateBody] = React.useState("");
    const [activeTab, setActiveTab] = React.useState(0);
    const [templateStatusFilter, setTemplateStatusFilter] =
        React.useState("ALL");
    const [templateParameterDefinitions, setTemplateParameterDefinitions] =
        React.useState<Compleo.IObject[]>([]);
    const [sourceTemplateName, setSourceTemplateName] = React.useState("");
    const [templateDetails, setTemplateDetails] =
        React.useState<Compleo.IObject | null>(null);

    const companyId = company?.companyId;
    const whatsappEnabled = company?.whatsappEnabled === true;
    const showPreRegistrationSettings =
        canShowWhatsappPreRegistrationSettings(
            whatsappEnabled,
            company?.whatsappApplicationEnabled === true
        );
    const companyPreRegistrationEnabled =
        company?.whatsappPreRegistrationEnabled === true;
    const [whatsappPreRegistrationEnabled, setWhatsappPreRegistrationEnabled] =
        React.useState(companyPreRegistrationEnabled);
    const canManagePreRegistrationRollout =
        isFeatureAuthorized("ManageWhatsappSettings") &&
        (isFeatureAuthorized("AddUser") ||
            isFeatureAuthorized("EditUser") ||
            isFeatureAuthorized("ManageJobTitle"));

    React.useEffect(() => {
        setWhatsappPreRegistrationEnabled(companyPreRegistrationEnabled);
    }, [companyPreRegistrationEnabled]);

    React.useEffect(() => {
        if (!showPreRegistrationSettings && activeTab === 2) {
            setActiveTab(0);
        }
    }, [activeTab, showPreRegistrationSettings]);

    const [connectionState, fetchConnection, invalidateConnection] =
        useApiCache(
            "/whatsapp/connection",
            "post",
            { companyId },
            true,
            1000 * 30
        );
    const [
        globalTemplatesState,
        fetchGlobalTemplates,
        invalidateGlobalTemplates
    ] = useApiCache(
        "/whatsapp/globaltemplates",
        "post",
        { companyId },
        true,
        1000 * 30
    );
    const [
        companyTemplatesState,
        fetchCompanyTemplates,
        invalidateCompanyTemplates
    ] = useApiCache(
        "/whatsapp/companytemplates",
        "post",
        { companyId },
        true,
        1000 * 30
    );
    const [phoneNumbersState, fetchPhoneNumbers, invalidatePhoneNumbers] =
        useApiCache(
            "/whatsapp/phonenumbers",
            "post",
            { companyId },
            true,
            1000 * 30
        );
    const fetchConnectionRef = React.useRef(fetchConnection);
    const fetchGlobalTemplatesRef = React.useRef(fetchGlobalTemplates);
    const fetchCompanyTemplatesRef = React.useRef(fetchCompanyTemplates);
    const fetchPhoneNumbersRef = React.useRef(fetchPhoneNumbers);
    const templateRevisionFormRef = React.useRef<HTMLDivElement | null>(null);
    const templateNameInputRef = React.useRef<HTMLInputElement | null>(null);

    React.useEffect(() => {
        fetchConnectionRef.current = fetchConnection;
    }, [fetchConnection]);

    React.useEffect(() => {
        fetchGlobalTemplatesRef.current = fetchGlobalTemplates;
    }, [fetchGlobalTemplates]);

    React.useEffect(() => {
        fetchCompanyTemplatesRef.current = fetchCompanyTemplates;
    }, [fetchCompanyTemplates]);

    React.useEffect(() => {
        fetchPhoneNumbersRef.current = fetchPhoneNumbers;
    }, [fetchPhoneNumbers]);

    const fetchedWhatsappConnection = getWhatsappApiResponseData(
        connectionState.response
    );
    const whatsappConnection = (connectionOverride ||
        fetchedWhatsappConnection ||
        {}) as Compleo.IObject;
    const canSendWhatsappMessages =
        whatsappConnection.connected === true &&
        whatsappConnection.canSendMessages !== false;
    const phoneNumbersResponse = getWhatsappApiResponseData(
        phoneNumbersState.response
    );
    const phoneNumbers = React.useMemo(
        () => (phoneNumbersResponse.phoneNumbers || []) as Compleo.IObject[],
        [phoneNumbersResponse.phoneNumbers]
    );
    const activePhoneNumberId = String(
        phoneNumbersResponse.activePhoneNumberId || ""
    );
    const selectedPhoneNumber = React.useMemo(
        () =>
            phoneNumbers.find(
                (phoneNumber) =>
                    String(phoneNumber.id || "") === selectedPhoneNumberId
            ),
        [phoneNumbers, selectedPhoneNumberId]
    );
    const globalTemplates = React.useMemo(
        () =>
            (getWhatsappApiResponseData(globalTemplatesState.response)
                .templates || []) as Compleo.IObject[],
        [globalTemplatesState.response]
    );
    const companyTemplates = React.useMemo(
        () =>
            (getWhatsappApiResponseData(companyTemplatesState.response)
                .templates || []) as Compleo.IObject[],
        [companyTemplatesState.response]
    );
    const filteredCompanyTemplates = React.useMemo(() => {
        if (templateStatusFilter === "ALL") {
            return companyTemplates;
        }

        return companyTemplates.filter(
            (template) =>
                String(template.status || "").toUpperCase() ===
                templateStatusFilter
        );
    }, [companyTemplates, templateStatusFilter]);
    const selectedGlobalTemplate = React.useMemo(
        () =>
            globalTemplates.find(
                (template) =>
                    String(template.key || "") === selectedGlobalTemplateKey
            ),
        [globalTemplates, selectedGlobalTemplateKey]
    );
    const whatsappConnectionDisplayName = String(
        whatsappConnection.verifiedName ||
            whatsappConnection.displayPhoneNumber ||
            whatsappConnection.phoneNumberId ||
            ""
    );
    const whatsappConnectionStatusText =
        whatsappConnection.connected === true && whatsappConnectionDisplayName
            ? `${labels.connected}: ${whatsappConnectionDisplayName}`
            : whatsappConnection.connected === true
              ? labels.connected
              : labels.notConnected;
    const webhookSubscriptionStatus = String(
        whatsappConnection.webhookSubscriptionStatus || "UNKNOWN"
    ).toUpperCase();
    const webhookSubscriptionIsSubscribed =
        webhookSubscriptionStatus === "SUBSCRIBED";
    const webhookSubscriptionIsUnverified =
        webhookSubscriptionStatus === "SUBSCRIBED_UNVERIFIED";
    const webhookSubscriptionText = webhookSubscriptionIsSubscribed
        ? labels.webhookSubscriptionSubscribed
        : webhookSubscriptionIsUnverified
          ? labels.webhookSubscriptionUnverified
          : webhookSubscriptionStatus === "FAILED"
            ? labels.webhookSubscriptionFailed
            : labels.webhookSubscriptionUnknown;

    const formatTemplateLastUpdate = React.useCallback(
        (template: Compleo.IObject) =>
            transformDBData({
                value: getTemplateLastUpdateValue(template),
                t,
                language: i18n.language,
                replaceOption: "-",
                customDateFormat: "dateWithTime"
            }),
        [i18n.language, t]
    );

    const applyGlobalTemplateSelection = React.useCallback(
        (templateKey: string) => {
            const nextTemplate = globalTemplates.find(
                (template) => String(template.key || "") === templateKey
            );

            setSelectedGlobalTemplateKey(templateKey);

            if (!nextTemplate) {
                setGlobalTemplateName("");
                setGlobalTemplateBody("");
                setTemplateParameterDefinitions([]);
                setSourceTemplateName("");
                return;
            }

            setGlobalTemplateName(String(nextTemplate.name || ""));
            setGlobalTemplateBody(String(nextTemplate.bodyText || ""));
            setTemplateParameterDefinitions(
                buildParameterDefinitions({
                    bodyText: String(nextTemplate.bodyText || ""),
                    fallbackLabels: nextTemplate.parameterLabels || [],
                    fallbackExamples: nextTemplate.parameterExamples || []
                })
            );
            setSourceTemplateName("");
        },
        [globalTemplates]
    );

    React.useEffect(() => {
        if (companyId) {
            fetchConnectionRef.current({ companyId });
        }
    }, [companyId]);

    React.useEffect(() => {
        if (companyId && whatsappEnabled) {
            fetchGlobalTemplatesRef.current({ companyId });
            fetchCompanyTemplatesRef.current({ companyId });
        }
    }, [companyId, whatsappEnabled]);

    React.useEffect(() => {
        if (
            companyId &&
            whatsappEnabled &&
            whatsappConnection.connected === true
        ) {
            fetchPhoneNumbersRef.current({ companyId });
        }
    }, [companyId, whatsappConnection.connected, whatsappEnabled]);

    React.useEffect(() => {
        setSelectedPhoneNumberId((currentPhoneNumberId) => {
            if (phoneNumbers.length === 0) {
                return "";
            }

            const selectedPhone =
                phoneNumbers.find(
                    (phoneNumber) => phoneNumber.selected === true
                ) || phoneNumbers[0];
            const currentPhoneStillExists = phoneNumbers.some(
                (phoneNumber) =>
                    String(phoneNumber.id || "") === currentPhoneNumberId
            );

            if (activePhoneNumberId) {
                return activePhoneNumberId;
            }

            if (!currentPhoneNumberId || !currentPhoneStillExists) {
                return String(selectedPhone.id || "");
            }

            return currentPhoneNumberId;
        });
    }, [activePhoneNumberId, phoneNumbers]);

    React.useEffect(() => {
        if (!selectedGlobalTemplateKey && globalTemplates.length > 0) {
            applyGlobalTemplateSelection(String(globalTemplates[0].key || ""));
        }
    }, [
        applyGlobalTemplateSelection,
        globalTemplates,
        selectedGlobalTemplateKey
    ]);

    React.useEffect(() => {
        if (
            selectionSessionId &&
            selectionStep &&
            !selectedMetaOptionId &&
            selectionOptions.length > 0
        ) {
            setSelectedMetaOptionId(String(selectionOptions[0].id || ""));
        }
    }, [
        selectedMetaOptionId,
        selectionOptions,
        selectionSessionId,
        selectionStep
    ]);

    React.useEffect(() => {
        if (connectionState.status === "success") {
            setConnectionOverride(null);
        }
    }, [connectionState.response, connectionState.status]);

    const refreshWhatsappConnection = () => {
        if (!companyId) {
            return;
        }

        if (invalidateConnection) {
            invalidateConnection({ companyId });
        }

        fetchConnection({ companyId });

        if (whatsappEnabled && whatsappConnection.connected === true) {
            if (invalidatePhoneNumbers) {
                invalidatePhoneNumbers({ companyId });
            }
            fetchPhoneNumbers({ companyId });
        }
    };

    const refreshGlobalWhatsappTemplates = () => {
        if (!companyId || !whatsappEnabled) {
            return;
        }

        if (invalidateGlobalTemplates) {
            invalidateGlobalTemplates({ companyId });
        }

        fetchGlobalTemplates({ companyId });

        if (invalidateCompanyTemplates) {
            invalidateCompanyTemplates({ companyId });
        }

        fetchCompanyTemplates({ companyId });
    };

    const clearConnectionForm = () => {
        setAuthCode("");
        setOauthState("");
        setSelectionSessionId("");
        setSelectionStep("");
        setSelectionOptions([]);
        setSelectedMetaOptionId("");
        setRegistrationPin("");
    };

    const showConnectionResultMessage = (
        data: Compleo.IObject,
        successLabel: string,
        failureLabel = labels.resubscribeWebhooksFailure
    ) => {
        const subscriptionStatus = String(
            data.webhookSubscriptionStatus || ""
        ).toUpperCase();
        const subscriptionNeedsAttention =
            subscriptionStatus === "FAILED" ||
            subscriptionStatus === "SUBSCRIBED_UNVERIFIED";

        showMessage(
            subscriptionStatus === "FAILED"
                ? failureLabel
                : subscriptionStatus === "SUBSCRIBED_UNVERIFIED"
                  ? labels.webhookSubscriptionUnverified
                  : successLabel,
            subscriptionNeedsAttention ? "warning" : "success"
        );
    };

    const applyConnectedResponse = (data: Compleo.IObject) => {
        if (data.connected !== true) {
            throw new Error(
                data.inactiveReason
                    ? `${labels.connectIncomplete} (${data.inactiveReason})`
                    : labels.connectIncomplete
            );
        }

        setConnectionOverride(data);
        refreshWhatsappConnection();
        if (invalidatePhoneNumbers) {
            invalidatePhoneNumbers({ companyId });
        }
        fetchPhoneNumbers({ companyId });
        clearConnectionForm();
        showConnectionResultMessage(
            data,
            labels.connectSuccess,
            labels.connectWebhookWarning
        );
    };

    const applySelectionRequiredResponse = (data: Compleo.IObject) => {
        setSelectionSessionId(data.selectionSessionId || "");
        setSelectionStep(data.step || "");
        setSelectionOptions(data.options || []);
        setSelectedMetaOptionId("");
        showMessage(labels.selectionRequired, "warning");
    };

    const completeWhatsappConnection = async (
        authCodeValue: string,
        stateValue: string
    ) => {
        const result = await apiDirectCall("/whatsapp/connect", "post", {
            companyId,
            authCode: authCodeValue,
            state: stateValue
        });
        const data = getWhatsappApiResponseData(result);

        if (data.selectionRequired === true) {
            applySelectionRequiredResponse(data);
            return;
        }

        applyConnectedResponse(data);
    };

    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const storedError = consumeStoredWhatsappOAuthError();
        const urlError =
            getWhatsappOAuthErrorFromSearch(window.location.search) ||
            storedError;

        if (urlError) {
            const message = formatWhatsappOAuthErrorMessage(urlError);
            const fallback =
                urlError.error === "access_denied"
                    ? labels.oauthDenied
                    : labels.oauthMetaUnavailable;

            showMessage(message || fallback, "error");
            return;
        }

        const storedCallback = consumeStoredWhatsappOAuthCallback();
        const code = params.get("code") || storedCallback?.code;
        const state = params.get("state") || storedCallback?.state;

        if (code) {
            setAuthCode(code);
        }

        if (state) {
            setOauthState(state);
        }

        if (code && state) {
            setPendingOAuthCallback({
                code,
                state
            });
        }
    }, []);

    React.useEffect(() => {
        if (
            !pendingOAuthCallback ||
            !companyId ||
            !whatsappEnabled ||
            isConnectingWhatsapp
        ) {
            return;
        }

        const executeCallbackConnection = async () => {
            try {
                setIsConnectingWhatsapp(true);
                await completeWhatsappConnection(
                    String(pendingOAuthCallback.code || ""),
                    String(pendingOAuthCallback.state || "")
                );
                setPendingOAuthCallback(null);
            } catch (error) {
                setPendingOAuthCallback(null);
                showMessage(
                    getApiErrorMessage(error, labels.connectSuccess),
                    "error"
                );
            } finally {
                setIsConnectingWhatsapp(false);
            }
        };

        void executeCallbackConnection();
    }, [
        companyId,
        isConnectingWhatsapp,
        pendingOAuthCallback,
        whatsappEnabled
    ]);

    const handleToggleWhatsappEnabled = async () => {
        if (!companyId) {
            return;
        }

        try {
            setIsTogglingWhatsappEnabled(true);
            await apiDirectCall("/company/updatecompanysetup", "post", {
                companyId,
                whatsappEnabled: !whatsappEnabled,
                operationType: ["whatsappEnabled"]
            });
            company.whatsappEnabled = !whatsappEnabled;
            await reloadCompanyAndPermissionData();
            refreshWhatsappConnection();
            showMessage(labels.toggleEnabledSuccess, "success");
        } catch (error) {
            showMessage(
                getApiErrorMessage(error, labels.toggleEnabledSuccess),
                "error"
            );
        } finally {
            setIsTogglingWhatsappEnabled(false);
        }
    };

    const updatePreRegistrationRollout = async (enabled: boolean) => {
        if (!companyId) {
            return;
        }
        setIsTogglingPreRegistrationRollout(true);
        try {
            await applyWhatsappPreRegistrationRolloutChange({
                enabled,
                requestChange: () =>
                    updateWhatsappPreRegistrationRollout({
                        companyId,
                        enabled
                    }),
                setFeatureActive: setWhatsappPreRegistrationEnabled,
                reloadCompanyAndPermissionData
            });
        } finally {
            setIsTogglingPreRegistrationRollout(false);
        }
    };

    const handleGenerateWhatsappAuthUrl = async () => {
        if (!companyId) {
            return;
        }

        if (!whatsappEnabled) {
            showMessage(labels.enableBeforeConnect, "warning");
            return;
        }

        try {
            setIsGeneratingWhatsappUrl(true);
            const result = await apiDirectCall("/whatsapp/getauthurl", "post", {
                companyId
            });
            const data = getWhatsappApiResponseData(result);
            setOauthState(data.state || "");
            setGeneratedAuthUrl(data.authUrl || "");
            setSelectionSessionId("");
            setSelectionStep("");
            setSelectionOptions([]);
            setSelectedMetaOptionId("");

            if (data.authUrl) {
                window.location.assign(data.authUrl);
            }

            showMessage(labels.generateSuccess, "success");
        } catch (error) {
            showMessage(
                getApiErrorMessage(error, labels.generateSuccess),
                "error"
            );
        } finally {
            setIsGeneratingWhatsappUrl(false);
        }
    };

    const handleConnectWhatsapp = async () => {
        if (!companyId) {
            return;
        }

        if (!authCode.trim() || !oauthState.trim()) {
            showMessage(labels.invalidConnect, "error");
            return;
        }

        try {
            setIsConnectingWhatsapp(true);
            await completeWhatsappConnection(
                authCode.trim(),
                oauthState.trim()
            );
        } catch (error) {
            showMessage(
                getApiErrorMessage(error, labels.connectSuccess),
                "error"
            );
        } finally {
            setIsConnectingWhatsapp(false);
        }
    };

    const handleContinueWhatsappSelection = async () => {
        if (!companyId || !selectionSessionId || !selectionStep) {
            return;
        }

        if (!selectedMetaOptionId) {
            showMessage(labels.invalidSelection, "error");
            return;
        }

        const selectionPayload: Compleo.IObject = {
            companyId,
            selectionSessionId
        };

        if (selectionStep === "BUSINESS") {
            selectionPayload.businessId = selectedMetaOptionId;
        }

        if (selectionStep === "WABA") {
            selectionPayload.wabaId = selectedMetaOptionId;
        }

        if (selectionStep === "PHONE_NUMBER") {
            selectionPayload.phoneNumberId = selectedMetaOptionId;
        }

        try {
            setIsConnectingWhatsapp(true);
            const result = await apiDirectCall(
                "/whatsapp/connectselection",
                "post",
                selectionPayload
            );
            const data = getWhatsappApiResponseData(result);
            if (data.selectionRequired === true) {
                applySelectionRequiredResponse(data);
                return;
            }

            applyConnectedResponse(data);
        } catch (error) {
            showMessage(
                getApiErrorMessage(error, labels.connectSuccess),
                "error"
            );
        } finally {
            setIsConnectingWhatsapp(false);
        }
    };

    const getSelectionLabel = () => {
        if (selectionStep === "BUSINESS") {
            return labels.selectMetaBusiness;
        }

        if (selectionStep === "WABA") {
            return labels.selectMetaWaba;
        }

        return labels.selectMetaPhone;
    };

    const refreshWhatsappPhoneNumbers = () => {
        if (!companyId || whatsappConnection.connected !== true) {
            return;
        }

        if (invalidatePhoneNumbers) {
            invalidatePhoneNumbers({ companyId });
        }

        fetchPhoneNumbers({ companyId });
    };

    const handleSelectWhatsappPhoneNumber = async () => {
        if (!companyId || !selectedPhoneNumberId) {
            return;
        }

        try {
            setIsSelectingWhatsappPhoneNumber(true);
            const result = await apiDirectCall(
                "/whatsapp/selectphonenumber",
                "post",
                {
                    companyId,
                    phoneNumberId: selectedPhoneNumberId
                }
            );
            const data = getWhatsappApiResponseData(result);
            setConnectionOverride(data);
            refreshWhatsappConnection();
            refreshWhatsappPhoneNumbers();
            showConnectionResultMessage(data, labels.phoneNumberSelected);
        } catch (error) {
            showMessage(
                getApiErrorMessage(error, labels.phoneNumberSelected),
                "error"
            );
        } finally {
            setIsSelectingWhatsappPhoneNumber(false);
        }
    };

    const handleRegisterWhatsappPhoneNumber = async () => {
        if (!companyId || !selectedPhoneNumberId) {
            return;
        }

        if (!/^\d{6}$/.test(registrationPin.trim())) {
            showMessage(labels.invalidRegistrationPin, "error");
            return;
        }

        try {
            setIsRegisteringWhatsappPhoneNumber(true);
            const result = await apiDirectCall(
                "/whatsapp/registerphonenumber",
                "post",
                {
                    companyId,
                    phoneNumberId: selectedPhoneNumberId,
                    pin: registrationPin.trim()
                }
            );
            const data = getWhatsappApiResponseData(result);
            if (data.connection) {
                setConnectionOverride(data.connection);
            }
            setRegistrationPin("");
            refreshWhatsappConnection();
            refreshWhatsappPhoneNumbers();
            showConnectionResultMessage(
                data.connection || data,
                labels.phoneNumberRegistered,
                labels.connectWebhookWarning
            );
        } catch (error) {
            showMessage(
                getApiErrorMessage(error, labels.phoneNumberRegistered),
                "error"
            );
        } finally {
            setIsRegisteringWhatsappPhoneNumber(false);
        }
    };

    const handleSubscribeWabaWebhooks = async () => {
        if (!companyId) {
            return;
        }

        try {
            setIsSubscribingWabaWebhooks(true);
            const result = await apiDirectCall(
                "/whatsapp/subscribewaba",
                "post",
                {
                    companyId
                }
            );
            const data = getWhatsappApiResponseData(result);
            setConnectionOverride(data);
            refreshWhatsappConnection();
            showConnectionResultMessage(
                data,
                labels.resubscribeWebhooksSuccess
            );
        } catch (error) {
            showMessage(
                getApiErrorMessage(error, labels.resubscribeWebhooksFailure),
                "error"
            );
        } finally {
            setIsSubscribingWabaWebhooks(false);
        }
    };

    const handleSyncWhatsappTemplates = async () => {
        if (!companyId) {
            return;
        }

        try {
            setIsSyncingWhatsappTemplates(true);
            await apiDirectCall("/whatsapp/synctemplates", "post", {
                companyId
            });
            refreshGlobalWhatsappTemplates();
            showMessage(labels.syncSuccess, "success");
        } catch (error) {
            showMessage(getApiErrorMessage(error, labels.syncSuccess), "error");
        } finally {
            setIsSyncingWhatsappTemplates(false);
        }
    };

    const handleTemplateBodyChange = (bodyText: string) => {
        setGlobalTemplateBody(bodyText);
        setTemplateParameterDefinitions((currentDefinitions) =>
            buildParameterDefinitions({
                bodyText,
                previousDefinitions: currentDefinitions,
                fallbackLabels: selectedGlobalTemplate?.parameterLabels || [],
                fallbackExamples:
                    selectedGlobalTemplate?.parameterExamples || [],
                componentType: "body",
                messageType:
                    templateCreationMode === "CUSTOM"
                        ? customTemplateMessageType
                        : undefined
            })
        );
    };

    const handleTemplateHeaderChange = (headerText: string) => {
        setCustomTemplateHeader(headerText);
        setTemplateParameterDefinitions((currentDefinitions) => {
            const bodyDefinitions = currentDefinitions.filter(
                (definition) =>
                    !definition.componentType ||
                    definition.componentType === "body"
            );
            const headerDefinitions = buildParameterDefinitions({
                bodyText: headerText,
                previousDefinitions: currentDefinitions,
                componentType: "header",
                messageType: customTemplateMessageType
            });

            return [...headerDefinitions, ...bodyDefinitions];
        });
    };

    const rebuildCustomTemplateParameterDefinitions = (
        messageType: string,
        headerText = customTemplateHeader,
        bodyText = globalTemplateBody
    ) => {
        setTemplateParameterDefinitions((currentDefinitions) => [
            ...buildParameterDefinitions({
                bodyText: headerText,
                previousDefinitions: currentDefinitions,
                componentType: "header",
                messageType
            }),
            ...buildParameterDefinitions({
                bodyText,
                previousDefinitions: currentDefinitions,
                componentType: "body",
                messageType
            })
        ]);
    };

    const handleTemplateParameterDefinitionChange = (
        index: number,
        field: string,
        value: string,
        componentType = "body"
    ) => {
        setTemplateParameterDefinitions((currentDefinitions) =>
            currentDefinitions.map((definition) =>
                Number(definition.index) === index &&
                String(definition.componentType || "body") === componentType
                    ? {
                          ...definition,
                          [field]: value
                      }
                    : definition
            )
        );
    };

    const handleTemplateParameterSourceChange = (
        index: number,
        componentType: string,
        sourceKey: string
    ) => {
        const catalogItem = getParameterCatalogItem(
            customTemplateMessageType,
            sourceKey
        );

        setTemplateParameterDefinitions((currentDefinitions) =>
            currentDefinitions.map((definition) =>
                Number(definition.index) === index &&
                String(definition.componentType || "body") === componentType
                    ? {
                          ...definition,
                          sourceKey,
                          sourceType: catalogItem?.sourceType || "SYSTEM",
                          label: catalogItem?.label || definition.label,
                          example: catalogItem?.example || definition.example
                      }
                    : definition
            )
        );
    };

    const validateTemplateParameters = () => {
        const indexes = getPlaceholderIndexesFromText(globalTemplateBody);

        for (let position = 0; position < indexes.length; position += 1) {
            if (indexes[position] !== position + 1) {
                showMessage(labels.invalidParameterSequence, "error");
                return false;
            }
        }

        const missingDefinition = templateParameterDefinitions.some(
            (definition) =>
                !String(definition.label || "").trim() ||
                !String(definition.example || "").trim()
        );

        if (missingDefinition) {
            showMessage(labels.requiredParameters, "error");
            return false;
        }

        return true;
    };

    const handleCreateTemplateRevision = (template: Compleo.IObject) => {
        const sourceGlobalTemplateKey = String(
            template.sourceGlobalTemplateKey || ""
        );
        const templateName = String(
            template.templateName || template.name || labels.templateName
        );

        if (!sourceGlobalTemplateKey) {
            showMessage(labels.cannotReviseTemplate, "warning");
            return;
        }

        setTemplateCreationMode("SUGGESTED");
        setSelectedGlobalTemplateKey(sourceGlobalTemplateKey);
        setGlobalTemplateName(getNextRevisionName(template, companyTemplates));
        const bodyText = getTemplateBodyText(template);
        setGlobalTemplateBody(bodyText);
        const examples = getTemplateParameterExamples(template);
        setTemplateParameterDefinitions(
            buildParameterDefinitions({
                bodyText,
                previousDefinitions: template.parameterDefinitions || [],
                fallbackLabels:
                    template.parameterLabels ||
                    selectedGlobalTemplate?.parameterLabels ||
                    [],
                fallbackExamples:
                    examples || selectedGlobalTemplate?.parameterExamples || []
            })
        );
        setSourceTemplateName(
            String(template.templateName || template.name || "")
        );
        showMessage(
            labels.revisionPrepared.replace("{{templateName}}", templateName),
            "success"
        );
        window.requestAnimationFrame(() => {
            templateRevisionFormRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start"
            });
            templateNameInputRef.current?.focus();
        });
    };

    const handleActivateTemplateForSending = async (
        template: Compleo.IObject
    ) => {
        const templateName = String(
            template.templateName || template.name || ""
        );

        if (!companyId || !templateName) {
            return;
        }

        try {
            setIsActivatingTemplateName(templateName);
            await apiDirectCall("/whatsapp/activatecompanytemplate", "post", {
                companyId,
                templateName,
                language: template.language || "pt_BR"
            });
            refreshGlobalWhatsappTemplates();
            showMessage(labels.activateTemplateSuccess, "success");
        } catch (error) {
            showMessage(
                getApiErrorMessage(error, labels.activateTemplateSuccess),
                "error"
            );
        } finally {
            setIsActivatingTemplateName("");
        }
    };

    const handleTemplateCreationModeChange = (mode: string) => {
        setTemplateCreationMode(mode);
        setSourceTemplateName("");

        if (mode === "CUSTOM") {
            setSelectedGlobalTemplateKey("");
            setGlobalTemplateName("");
            setGlobalTemplateBody("");
            setCustomTemplateHeader("");
            setCustomTemplateFooter("");
            setCustomTemplateButtons([]);
            setTemplateParameterDefinitions([]);
            return;
        }

        if (globalTemplates.length > 0) {
            applyGlobalTemplateSelection(String(globalTemplates[0].key || ""));
        }
    };

    const handleCustomTemplateMessageTypeChange = (messageType: string) => {
        setCustomTemplateMessageType(messageType);
        rebuildCustomTemplateParameterDefinitions(messageType);
    };

    const ensureMarketingOptOutControls = () => {
        setCustomTemplateFooter((currentFooter) =>
            currentFooter.trim()
                ? currentFooter
                : WHATSAPP_MARKETING_OPT_OUT_FOOTER
        );
        setCustomTemplateButtons((currentButtons) => {
            const hasOptOutButton = currentButtons.some((button) =>
                String(button.text || "")
                    .toUpperCase()
                    .includes("PARAR")
            );

            if (hasOptOutButton) {
                return currentButtons;
            }

            return [
                ...currentButtons,
                {
                    type: "QUICK_REPLY",
                    text: WHATSAPP_MARKETING_OPT_OUT_BUTTON_TEXT
                }
            ];
        });
    };

    const handleCustomTemplateCategoryChange = (category: string) => {
        setCustomTemplateCategory(category);
        if (category === "MARKETING") {
            ensureMarketingOptOutControls();
        }
    };

    const handleAddCustomTemplateButton = () => {
        setCustomTemplateButtons((currentButtons) => [
            ...currentButtons,
            {
                type: "QUICK_REPLY",
                text: ""
            }
        ]);
    };

    const handleCustomTemplateButtonChange = (
        index: number,
        field: string,
        value: string
    ) => {
        setCustomTemplateButtons((currentButtons) =>
            currentButtons.map((button, buttonIndex) =>
                buttonIndex === index
                    ? {
                          ...button,
                          [field]: value
                      }
                    : button
            )
        );
    };

    const handleRemoveCustomTemplateButton = (index: number) => {
        setCustomTemplateButtons((currentButtons) =>
            currentButtons.filter(
                (_button, buttonIndex) => buttonIndex !== index
            )
        );
    };

    const handleCreateWhatsappTemplate = async () => {
        if (!companyId) {
            return;
        }

        if (
            templateCreationMode === "SUGGESTED" &&
            !selectedGlobalTemplateKey
        ) {
            showMessage(labels.requiredTemplate, "error");
            return;
        }

        if (!validateTemplateParameters()) {
            return;
        }

        try {
            setIsCreatingWhatsappTemplate(true);
            if (templateCreationMode === "CUSTOM") {
                await apiDirectCall("/whatsapp/createcompanytemplate", "post", {
                    companyId,
                    templateName: globalTemplateName,
                    category: customTemplateCategory,
                    language: customTemplateLanguage,
                    messageType: customTemplateMessageType,
                    headerText: customTemplateHeader,
                    bodyText: globalTemplateBody,
                    footerText: customTemplateFooter,
                    buttons: customTemplateButtons,
                    parameterDefinitions: templateParameterDefinitions
                });
            } else {
                await apiDirectCall("/whatsapp/createglobaltemplate", "post", {
                    companyId,
                    templateKey: selectedGlobalTemplateKey,
                    templateName: globalTemplateName,
                    bodyText: globalTemplateBody,
                    parameterDefinitions: templateParameterDefinitions,
                    sourceTemplateName
                });
            }
            setSourceTemplateName("");
            refreshGlobalWhatsappTemplates();
            showMessage(labels.templateCreated, "success");
        } catch (error) {
            showMessage(
                getApiErrorMessage(error, labels.templateCreated),
                "error"
            );
        } finally {
            setIsCreatingWhatsappTemplate(false);
        }
    };

    const renderStatusChip = (status: string) => {
        const normalizedStatus = String(status || "UNKNOWN").toUpperCase();
        const backgroundByStatus: Record<string, string> = {
            APPROVED: "#e8f5e9",
            PENDING: "#fff8e1",
            REJECTED: "#ffebee",
            PAUSED: "#eeeeee",
            DISABLED: "#eeeeee",
            DELETED: "#eeeeee"
        };
        const colorByStatus: Record<string, string> = {
            APPROVED: "#1b5e20",
            PENDING: "#8a5a00",
            REJECTED: "#b71c1c",
            PAUSED: "#424242",
            DISABLED: "#424242",
            DELETED: "#424242"
        };

        return (
            <Chip
                size="small"
                label={normalizedStatus}
                style={{
                    backgroundColor:
                        backgroundByStatus[normalizedStatus] || "#eeeeee",
                    color: colorByStatus[normalizedStatus] || "#424242"
                }}
            />
        );
    };

    return (
        <Page
            featureName={labels.featureName}
            featureMainDescription={labels.featureDescription}
            maxWidth={false}
        >
            <Box width="100%">
                <Paper elevation={0} style={{ width: "100%" }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, nextTab) => setActiveTab(nextTab)}
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        <Tab label={labels.configurationTab} />
                        <Tab label={labels.templatesTab} />
                        {showPreRegistrationSettings && (
                            <Tab label={labels.preRegistrationTab} />
                        )}
                    </Tabs>
                </Paper>

                {activeTab === 0 && (
                    <Box mt={3}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={5}>
                                <Paper elevation={0} style={{ padding: 24 }}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        mb={2}
                                    >
                                        <Box mr={1}>
                                            <WhatsAppIcon color="primary" />
                                        </Box>
                                        <Typography variant="h6">
                                            {labels.statusTitle}
                                        </Typography>
                                    </Box>
                                    <Alert
                                        severity={
                                            whatsappConnection.connected ===
                                            true
                                                ? "success"
                                                : whatsappEnabled
                                                  ? "info"
                                                  : "warning"
                                        }
                                    >
                                        {whatsappConnectionStatusText}
                                    </Alert>
                                    <Box mt={2}>
                                        <Chip
                                            color={
                                                whatsappEnabled
                                                    ? "primary"
                                                    : "default"
                                            }
                                            label={
                                                whatsappEnabled
                                                    ? labels.featureEnabled
                                                    : labels.featureDisabled
                                            }
                                        />
                                    </Box>
                                    {whatsappConnection.connected === true && (
                                        <Box mt={2}>
                                            <Alert
                                                severity={
                                                    webhookSubscriptionIsSubscribed
                                                        ? "success"
                                                        : webhookSubscriptionIsUnverified
                                                          ? "warning"
                                                          : webhookSubscriptionStatus ===
                                                              "FAILED"
                                                            ? "warning"
                                                            : "info"
                                                }
                                            >
                                                <Typography variant="body2">
                                                    <strong>
                                                        {
                                                            labels.webhookSubscriptionTitle
                                                        }
                                                        :
                                                    </strong>{" "}
                                                    {webhookSubscriptionText}
                                                </Typography>
                                                {!webhookSubscriptionIsSubscribed && (
                                                    <Box mt={1}>
                                                        <Button
                                                            onClick={
                                                                handleSubscribeWabaWebhooks
                                                            }
                                                            color="secondary"
                                                            variant="outlined"
                                                            size="small"
                                                            disabled={
                                                                isSubscribingWabaWebhooks
                                                            }
                                                        >
                                                            {isSubscribingWabaWebhooks
                                                                ? labels.resubscribingWebhooks
                                                                : labels.resubscribeWebhooks}
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Alert>
                                        </Box>
                                    )}
                                    {whatsappConnection.hasStoredConnection ===
                                        true &&
                                        !whatsappEnabled && (
                                            <Box mt={2}>
                                                <Alert severity="warning">
                                                    {
                                                        labels.disabledWithStoredConnection
                                                    }
                                                </Alert>
                                            </Box>
                                        )}
                                    {connectionState.status === "error" && (
                                        <Box mt={2}>
                                            <Alert severity="error">
                                                {getApiErrorMessage(
                                                    connectionState.exception,
                                                    labels.notConnected
                                                )}
                                            </Alert>
                                        </Box>
                                    )}
                                    <Box
                                        mt={3}
                                        display="flex"
                                        flexWrap="wrap"
                                        style={{ gap: 8 }}
                                    >
                                        <Button
                                            onClick={
                                                handleToggleWhatsappEnabled
                                            }
                                            color="primary"
                                            variant={
                                                whatsappEnabled
                                                    ? "outlined"
                                                    : "contained"
                                            }
                                            disabled={isTogglingWhatsappEnabled}
                                        >
                                            {whatsappEnabled
                                                ? labels.disable
                                                : labels.enable}
                                        </Button>
                                        <Button
                                            onClick={refreshWhatsappConnection}
                                            color="secondary"
                                            variant="outlined"
                                        >
                                            {labels.refreshStatus}
                                        </Button>
                                    </Box>
                                </Paper>

                                {whatsappConnection.connected === true && (
                                    <Box mt={3}>
                                        <Paper
                                            elevation={0}
                                            style={{ padding: 24 }}
                                        >
                                            <Typography variant="h6">
                                                {labels.phoneNumbersTitle}
                                            </Typography>
                                            <Box mt={1}>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {
                                                        labels.phoneNumbersDescription
                                                    }
                                                </Typography>
                                            </Box>
                                            {phoneNumbersState.status ===
                                                "error" && (
                                                <Box mt={2}>
                                                    <Alert severity="error">
                                                        {getApiErrorMessage(
                                                            phoneNumbersState.exception,
                                                            labels.phoneNumbersTitle
                                                        )}
                                                    </Alert>
                                                </Box>
                                            )}
                                            <Box mt={2}>
                                                <TextField
                                                    select
                                                    label={
                                                        labels.phoneNumberField
                                                    }
                                                    variant="outlined"
                                                    fullWidth
                                                    value={
                                                        selectedPhoneNumberId
                                                    }
                                                    onChange={(event) => {
                                                        setSelectedPhoneNumberId(
                                                            event.target.value
                                                        );
                                                        setRegistrationPin("");
                                                    }}
                                                    disabled={
                                                        phoneNumbersState.status ===
                                                        "fetching"
                                                    }
                                                >
                                                    {phoneNumbers.map(
                                                        (phoneNumber) => (
                                                            <MenuItem
                                                                key={String(
                                                                    phoneNumber.id ||
                                                                        ""
                                                                )}
                                                                value={String(
                                                                    phoneNumber.id ||
                                                                        ""
                                                                )}
                                                            >
                                                                {[
                                                                    phoneNumber.displayName,
                                                                    phoneNumber.phoneNumber,
                                                                    phoneNumber.status
                                                                ]
                                                                    .filter(
                                                                        Boolean
                                                                    )
                                                                    .join(
                                                                        " - "
                                                                    )}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </TextField>
                                            </Box>
                                            {selectedPhoneNumber && (
                                                <Box mt={2}>
                                                    <Box
                                                        display="flex"
                                                        flexWrap="wrap"
                                                        style={{ gap: 8 }}
                                                    >
                                                        <Chip
                                                            label={`${labels.phoneNumberStatus}: ${
                                                                selectedPhoneNumber.status ||
                                                                "UNKNOWN"
                                                            }`}
                                                            color={
                                                                selectedPhoneNumber.readyForMessaging
                                                                    ? "primary"
                                                                    : "default"
                                                            }
                                                        />
                                                        {selectedPhoneNumber.qualityRating && (
                                                            <Chip
                                                                label={`${labels.phoneNumberQuality}: ${selectedPhoneNumber.qualityRating}`}
                                                            />
                                                        )}
                                                    </Box>
                                                    <Box mt={2}>
                                                        <Alert
                                                            severity={
                                                                selectedPhoneNumber.readyForMessaging
                                                                    ? "success"
                                                                    : selectedPhoneNumber.status
                                                                      ? "warning"
                                                                      : "info"
                                                            }
                                                        >
                                                            {selectedPhoneNumber.readyForMessaging
                                                                ? labels.phoneNumberReady
                                                                : selectedPhoneNumber.status
                                                                  ? labels.phoneNumberPending
                                                                  : labels.phoneNumberUnknown}
                                                        </Alert>
                                                    </Box>
                                                    <Box
                                                        mt={2}
                                                        display="flex"
                                                        flexWrap="wrap"
                                                        style={{ gap: 8 }}
                                                    >
                                                        <Button
                                                            onClick={
                                                                handleSelectWhatsappPhoneNumber
                                                            }
                                                            color="primary"
                                                            variant="outlined"
                                                            disabled={
                                                                isSelectingWhatsappPhoneNumber ||
                                                                selectedPhoneNumberId ===
                                                                    whatsappConnection.phoneNumberId
                                                            }
                                                        >
                                                            {
                                                                labels.phoneNumberSelectAction
                                                            }
                                                        </Button>
                                                        <Button
                                                            onClick={
                                                                refreshWhatsappPhoneNumbers
                                                            }
                                                            color="secondary"
                                                            variant="outlined"
                                                        >
                                                            {
                                                                labels.refreshStatus
                                                            }
                                                        </Button>
                                                    </Box>
                                                    {selectedPhoneNumber.readyForMessaging ===
                                                        false && (
                                                        <Box mt={2}>
                                                            <Alert severity="info">
                                                                <Typography variant="body2">
                                                                    {
                                                                        labels.registrationPinHelp
                                                                    }
                                                                </Typography>
                                                                <Box mt={1}>
                                                                    <Typography variant="body2">
                                                                        {
                                                                            labels.registrationPinCreation
                                                                        }
                                                                    </Typography>
                                                                </Box>
                                                                <Box mt={1}>
                                                                    <Typography variant="body2">
                                                                        {
                                                                            labels.registrationPinStorage
                                                                        }
                                                                    </Typography>
                                                                </Box>
                                                                <Box mt={1}>
                                                                    <Typography variant="body2">
                                                                        {
                                                                            labels.registrationPinAttemptLimit
                                                                        }
                                                                    </Typography>
                                                                </Box>
                                                            </Alert>
                                                            <Box mt={2}>
                                                                <TextField
                                                                    label={
                                                                        labels.registrationPin
                                                                    }
                                                                    variant="outlined"
                                                                    type="text"
                                                                    value={
                                                                        registrationPin
                                                                    }
                                                                    onChange={(
                                                                        event
                                                                    ) =>
                                                                        setRegistrationPin(
                                                                            event.target.value
                                                                                .replace(
                                                                                    /\D/g,
                                                                                    ""
                                                                                )
                                                                                .slice(
                                                                                    0,
                                                                                    6
                                                                                )
                                                                        )
                                                                    }
                                                                    inputProps={{
                                                                        inputMode:
                                                                            "numeric",
                                                                        maxLength: 6
                                                                    }}
                                                                />
                                                            </Box>
                                                            <Box mt={2}>
                                                                <Button
                                                                    onClick={
                                                                        handleRegisterWhatsappPhoneNumber
                                                                    }
                                                                    color="primary"
                                                                    variant="contained"
                                                                    disabled={
                                                                        isRegisteringWhatsappPhoneNumber ||
                                                                        registrationPin.length !==
                                                                            6
                                                                    }
                                                                >
                                                                    {
                                                                        labels.registerPhoneNumber
                                                                    }
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Paper>
                                    </Box>
                                )}
                            </Grid>

                            <Grid item xs={12} md={7}>
                                <Paper elevation={0} style={{ padding: 24 }}>
                                    <Typography variant="h6">
                                        {labels.generateAuthUrl}
                                    </Typography>
                                    <Box
                                        mt={2}
                                        display="flex"
                                        flexWrap="wrap"
                                        style={{ gap: 8 }}
                                    >
                                        <Button
                                            onClick={
                                                handleGenerateWhatsappAuthUrl
                                            }
                                            color="primary"
                                            variant="contained"
                                            disabled={
                                                isGeneratingWhatsappUrl ||
                                                !whatsappEnabled
                                            }
                                        >
                                            {labels.generateAuthUrl}
                                        </Button>
                                        <Button
                                            onClick={
                                                handleSyncWhatsappTemplates
                                            }
                                            color="primary"
                                            variant="outlined"
                                            disabled={
                                                isSyncingWhatsappTemplates ||
                                                whatsappConnection.connected !==
                                                    true
                                            }
                                        >
                                            {labels.syncTemplates}
                                        </Button>
                                    </Box>
                                    <Box mt={2}>
                                        <TextField
                                            label={labels.generatedAuthUrl}
                                            variant="outlined"
                                            fullWidth
                                            value={generatedAuthUrl}
                                            InputProps={{
                                                readOnly: true
                                            }}
                                        />
                                    </Box>
                                    {generatedAuthUrl && (
                                        <Box mt={2}>
                                            <Button
                                                onClick={() =>
                                                    window.location.assign(
                                                        generatedAuthUrl
                                                    )
                                                }
                                                color="primary"
                                                variant="outlined"
                                            >
                                                {labels.openGeneratedAuthUrl}
                                            </Button>
                                        </Box>
                                    )}
                                    <Box mt={2}>
                                        <TextField
                                            label={labels.oauthState}
                                            variant="outlined"
                                            fullWidth
                                            value={oauthState}
                                            onChange={(event) =>
                                                setOauthState(
                                                    event.target.value
                                                )
                                            }
                                        />
                                    </Box>
                                    <Box mt={2}>
                                        <TextField
                                            label={labels.authCode}
                                            variant="outlined"
                                            fullWidth
                                            value={authCode}
                                            onChange={(event) =>
                                                setAuthCode(event.target.value)
                                            }
                                        />
                                    </Box>
                                    <Box mt={2}>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            {labels.authHelp}
                                        </Typography>
                                    </Box>
                                    <Box mt={2}>
                                        <Button
                                            onClick={handleConnectWhatsapp}
                                            color="primary"
                                            variant="contained"
                                            disabled={
                                                isConnectingWhatsapp ||
                                                !whatsappEnabled
                                            }
                                        >
                                            {labels.connect}
                                        </Button>
                                    </Box>
                                    {selectionSessionId && selectionStep && (
                                        <Box mt={3}>
                                            <Alert severity="info">
                                                {labels.selectionRequired}
                                            </Alert>
                                            <Box mt={2}>
                                                <TextField
                                                    select
                                                    label={getSelectionLabel()}
                                                    variant="outlined"
                                                    fullWidth
                                                    value={selectedMetaOptionId}
                                                    onChange={(event) =>
                                                        setSelectedMetaOptionId(
                                                            event.target.value
                                                        )
                                                    }
                                                >
                                                    <MenuItem value="">
                                                        {getSelectionLabel()}
                                                    </MenuItem>
                                                    {selectionOptions.map(
                                                        (option) => (
                                                            <MenuItem
                                                                key={String(
                                                                    option.id ||
                                                                        ""
                                                                )}
                                                                value={String(
                                                                    option.id ||
                                                                        ""
                                                                )}
                                                            >
                                                                {String(
                                                                    option.displayName ||
                                                                        option.name ||
                                                                        option.phoneNumber ||
                                                                        option.id ||
                                                                        ""
                                                                )}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </TextField>
                                            </Box>
                                            <Box mt={2}>
                                                <Button
                                                    onClick={
                                                        handleContinueWhatsappSelection
                                                    }
                                                    color="primary"
                                                    variant="contained"
                                                    disabled={
                                                        isConnectingWhatsapp ||
                                                        !selectedMetaOptionId
                                                    }
                                                >
                                                    {labels.continueSelection}
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {activeTab === 1 && (
                    <Box mt={3}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Paper elevation={0} style={{ padding: 24 }}>
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        flexWrap="wrap"
                                        style={{ gap: 12 }}
                                    >
                                        <Box>
                                            <Typography variant="h6">
                                                {labels.companyTemplatesTitle}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {
                                                    labels.companyTemplatesDescription
                                                }
                                            </Typography>
                                        </Box>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            style={{ gap: 8 }}
                                        >
                                            <TextField
                                                select
                                                label={labels.filterStatus}
                                                variant="outlined"
                                                size="small"
                                                value={templateStatusFilter}
                                                onChange={(event) =>
                                                    setTemplateStatusFilter(
                                                        event.target.value
                                                    )
                                                }
                                            >
                                                <MenuItem value="ALL">
                                                    {labels.allStatuses}
                                                </MenuItem>
                                                {[
                                                    "APPROVED",
                                                    "PENDING",
                                                    "REJECTED",
                                                    "PAUSED",
                                                    "DISABLED",
                                                    "DELETED"
                                                ].map((status) => (
                                                    <MenuItem
                                                        key={status}
                                                        value={status}
                                                    >
                                                        {status}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <Button
                                                onClick={
                                                    handleSyncWhatsappTemplates
                                                }
                                                color="primary"
                                                variant="outlined"
                                                disabled={
                                                    isSyncingWhatsappTemplates ||
                                                    whatsappConnection.connected !==
                                                        true
                                                }
                                            >
                                                {labels.syncTemplates}
                                            </Button>
                                        </Box>
                                    </Box>

                                    {companyTemplatesState.status ===
                                        "error" && (
                                        <Box mt={2}>
                                            <Alert severity="error">
                                                {getApiErrorMessage(
                                                    companyTemplatesState.exception,
                                                    labels.companyTemplatesTitle
                                                )}
                                            </Alert>
                                        </Box>
                                    )}

                                    <Box mt={2}>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>
                                                            {
                                                                labels.templateName
                                                            }
                                                        </TableCell>
                                                        <TableCell>
                                                            Status
                                                        </TableCell>
                                                        <TableCell>
                                                            {
                                                                labels.templateCurrentCategory
                                                            }
                                                        </TableCell>
                                                        <TableCell>
                                                            {
                                                                labels.templateRequestedCategory
                                                            }
                                                        </TableCell>
                                                        <TableCell>
                                                            {labels.quality}
                                                        </TableCell>
                                                        <TableCell>
                                                            {labels.language}
                                                        </TableCell>
                                                        <TableCell>
                                                            {labels.lastUpdate}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {labels.actions}
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {filteredCompanyTemplates.map(
                                                        (template) => (
                                                            <TableRow
                                                                key={`${template.templateName}-${template.language}`}
                                                            >
                                                                <TableCell>
                                                                    <Typography variant="body2">
                                                                        {String(
                                                                            template.templateName ||
                                                                                template.name ||
                                                                                ""
                                                                        )}
                                                                    </Typography>
                                                                    {template.globalTemplateLabel && (
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="textSecondary"
                                                                        >
                                                                            {
                                                                                labels.copiedFrom
                                                                            }
                                                                            :{" "}
                                                                            {String(
                                                                                template.globalTemplateLabel
                                                                            )}
                                                                        </Typography>
                                                                    )}
                                                                    {template.activeForSending ===
                                                                        true && (
                                                                        <Box
                                                                            mt={
                                                                                1
                                                                            }
                                                                        >
                                                                            <Chip
                                                                                size="small"
                                                                                color="primary"
                                                                                label={
                                                                                    labels.activeForSending
                                                                                }
                                                                            />
                                                                        </Box>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {renderStatusChip(
                                                                        String(
                                                                            template.status ||
                                                                                ""
                                                                        )
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {String(
                                                                        template.category ||
                                                                            ""
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {String(
                                                                        template.requestedCategory ||
                                                                            ""
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {String(
                                                                        template.qualityScore ||
                                                                            "-"
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {String(
                                                                        template.language ||
                                                                            ""
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {formatTemplateLastUpdate(
                                                                        template
                                                                    )}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Tooltip
                                                                        title={
                                                                            labels.viewDetails
                                                                        }
                                                                    >
                                                                        <IconButton
                                                                            size="small"
                                                                            aria-label={
                                                                                labels.viewDetails
                                                                            }
                                                                            style={createWhatsappActionButtonStyle(
                                                                                "#1976d2"
                                                                            )}
                                                                            onClick={() =>
                                                                                setTemplateDetails(
                                                                                    template
                                                                                )
                                                                            }
                                                                        >
                                                                            <VisibilityIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip
                                                                        title={
                                                                            template.sourceGlobalTemplateKey
                                                                                ? labels.createRevision
                                                                                : labels.cannotReviseTemplate
                                                                        }
                                                                    >
                                                                        <span>
                                                                            <IconButton
                                                                                size="small"
                                                                                aria-label={
                                                                                    labels.createRevision
                                                                                }
                                                                                style={createWhatsappActionButtonStyle(
                                                                                    "#f57c00",
                                                                                    !template.sourceGlobalTemplateKey
                                                                                )}
                                                                                onClick={() =>
                                                                                    handleCreateTemplateRevision(
                                                                                        template
                                                                                    )
                                                                                }
                                                                                disabled={
                                                                                    !template.sourceGlobalTemplateKey
                                                                                }
                                                                            >
                                                                                <FileCopyIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </span>
                                                                    </Tooltip>
                                                                    {template.status ===
                                                                        "APPROVED" && (
                                                                        <Tooltip
                                                                            title={
                                                                                labels.activateForSending
                                                                            }
                                                                        >
                                                                            <span>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    aria-label={
                                                                                        labels.activateForSending
                                                                                    }
                                                                                    style={createWhatsappActionButtonStyle(
                                                                                        "#2e7d32"
                                                                                    )}
                                                                                    disabled={
                                                                                        template.activeForSending ===
                                                                                            true ||
                                                                                        isActivatingTemplateName ===
                                                                                            String(
                                                                                                template.templateName ||
                                                                                                    template.name ||
                                                                                                    ""
                                                                                            )
                                                                                    }
                                                                                    onClick={() =>
                                                                                        handleActivateTemplateForSending(
                                                                                            template
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <CheckCircleIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </span>
                                                                        </Tooltip>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    )}
                                                    {filteredCompanyTemplates.length ===
                                                        0 && (
                                                        <TableRow>
                                                            <TableCell
                                                                colSpan={8}
                                                            >
                                                                <Alert severity="info">
                                                                    {
                                                                        labels.noCompanyTemplates
                                                                    }
                                                                </Alert>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12}>
                                <Paper
                                    elevation={0}
                                    style={{ padding: 24 }}
                                    ref={templateRevisionFormRef}
                                >
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={7}>
                                            <Typography variant="h6">
                                                {templateCreationMode ===
                                                "CUSTOM"
                                                    ? labels.customTemplateTitle
                                                    : labels.templatesTitle}
                                            </Typography>
                                            <Box mt={1}>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {templateCreationMode ===
                                                    "CUSTOM"
                                                        ? labels.customTemplateDescription
                                                        : labels.templatesDescription}
                                                </Typography>
                                            </Box>
                                            {sourceTemplateName && (
                                                <Box mt={2}>
                                                    <Alert severity="info">
                                                        {labels.revisionInfo}
                                                        <Box mt={1}>
                                                            <strong>
                                                                {
                                                                    labels.revisionSource
                                                                }
                                                                :
                                                            </strong>{" "}
                                                            {sourceTemplateName}
                                                        </Box>
                                                    </Alert>
                                                </Box>
                                            )}
                                            {globalTemplatesState.status ===
                                                "error" && (
                                                <Box mt={2}>
                                                    <Alert severity="error">
                                                        {getApiErrorMessage(
                                                            globalTemplatesState.exception,
                                                            labels.templatesTitle
                                                        )}
                                                    </Alert>
                                                </Box>
                                            )}
                                            <Box mt={2}>
                                                <TextField
                                                    select
                                                    label={
                                                        labels.templateCreationMode
                                                    }
                                                    variant="outlined"
                                                    fullWidth
                                                    value={templateCreationMode}
                                                    onChange={(event) =>
                                                        handleTemplateCreationModeChange(
                                                            event.target.value
                                                        )
                                                    }
                                                >
                                                    <MenuItem value="SUGGESTED">
                                                        {
                                                            labels.suggestedTemplateMode
                                                        }
                                                    </MenuItem>
                                                    <MenuItem value="CUSTOM">
                                                        {
                                                            labels.customTemplateMode
                                                        }
                                                    </MenuItem>
                                                </TextField>
                                            </Box>
                                            {templateCreationMode ===
                                                "CUSTOM" && (
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Box mt={2}>
                                                            <TextField
                                                                select
                                                                label={
                                                                    labels.messageType
                                                                }
                                                                variant="outlined"
                                                                fullWidth
                                                                value={
                                                                    customTemplateMessageType
                                                                }
                                                                onChange={(
                                                                    event
                                                                ) =>
                                                                    handleCustomTemplateMessageTypeChange(
                                                                        event
                                                                            .target
                                                                            .value
                                                                    )
                                                                }
                                                            >
                                                                {WHATSAPP_TEMPLATE_MESSAGE_TYPES.map(
                                                                    (type) => (
                                                                        <MenuItem
                                                                            key={
                                                                                type.key
                                                                            }
                                                                            value={
                                                                                type.key
                                                                            }
                                                                        >
                                                                            {
                                                                                type.label
                                                                            }
                                                                        </MenuItem>
                                                                    )
                                                                )}
                                                            </TextField>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <Box mt={2}>
                                                            <TextField
                                                                select
                                                                label={
                                                                    labels.templateCategory
                                                                }
                                                                variant="outlined"
                                                                fullWidth
                                                                value={
                                                                    customTemplateCategory
                                                                }
                                                                onChange={(
                                                                    event
                                                                ) =>
                                                                    handleCustomTemplateCategoryChange(
                                                                        event
                                                                            .target
                                                                            .value
                                                                    )
                                                                }
                                                            >
                                                                <MenuItem value="UTILITY">
                                                                    UTILITY
                                                                </MenuItem>
                                                                <MenuItem value="MARKETING">
                                                                    MARKETING
                                                                </MenuItem>
                                                            </TextField>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <Box mt={2}>
                                                            <TextField
                                                                label={
                                                                    labels.language
                                                                }
                                                                variant="outlined"
                                                                fullWidth
                                                                value={
                                                                    customTemplateLanguage
                                                                }
                                                                onChange={(
                                                                    event
                                                                ) =>
                                                                    setCustomTemplateLanguage(
                                                                        event
                                                                            .target
                                                                            .value
                                                                    )
                                                                }
                                                            />
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            )}
                                            {templateCreationMode ===
                                                "SUGGESTED" && (
                                                <Box mt={2}>
                                                    <TextField
                                                        select
                                                        label={
                                                            labels.templateField
                                                        }
                                                        variant="outlined"
                                                        fullWidth
                                                        value={
                                                            selectedGlobalTemplateKey
                                                        }
                                                        onChange={(event) =>
                                                            applyGlobalTemplateSelection(
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                        disabled={
                                                            globalTemplatesState.status ===
                                                                "fetching" ||
                                                            !whatsappEnabled
                                                        }
                                                    >
                                                        {globalTemplates.map(
                                                            (template) => (
                                                                <MenuItem
                                                                    key={String(
                                                                        template.key ||
                                                                            ""
                                                                    )}
                                                                    value={String(
                                                                        template.key ||
                                                                            ""
                                                                    )}
                                                                >
                                                                    {String(
                                                                        template.label ||
                                                                            ""
                                                                    )}
                                                                </MenuItem>
                                                            )
                                                        )}
                                                    </TextField>
                                                </Box>
                                            )}
                                            {templateCreationMode ===
                                                "SUGGESTED" &&
                                                selectedGlobalTemplate && (
                                                    <Box mt={2}>
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                        >
                                                            {String(
                                                                selectedGlobalTemplate.description ||
                                                                    ""
                                                            )}
                                                        </Typography>
                                                        <Box mt={1}>
                                                            <Typography
                                                                variant="caption"
                                                                color="textSecondary"
                                                            >
                                                                {
                                                                    labels.templateCategory
                                                                }
                                                                :{" "}
                                                                {String(
                                                                    selectedGlobalTemplate.category ||
                                                                        ""
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                            {templateCreationMode ===
                                                "CUSTOM" && (
                                                <Box mt={2}>
                                                    <TextField
                                                        label={
                                                            labels.headerText
                                                        }
                                                        variant="outlined"
                                                        fullWidth
                                                        value={
                                                            customTemplateHeader
                                                        }
                                                        onChange={(event) =>
                                                            handleTemplateHeaderChange(
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                    />
                                                </Box>
                                            )}
                                            <Box mt={2}>
                                                <TextField
                                                    label={labels.templateName}
                                                    variant="outlined"
                                                    fullWidth
                                                    inputRef={
                                                        templateNameInputRef
                                                    }
                                                    value={globalTemplateName}
                                                    onChange={(event) =>
                                                        setGlobalTemplateName(
                                                            event.target.value
                                                        )
                                                    }
                                                    disabled={
                                                        templateCreationMode ===
                                                            "SUGGESTED" &&
                                                        !selectedGlobalTemplate
                                                    }
                                                />
                                            </Box>
                                            <Box mt={2}>
                                                <TextField
                                                    label={labels.templateBody}
                                                    variant="outlined"
                                                    fullWidth
                                                    multiline
                                                    rows={5}
                                                    value={globalTemplateBody}
                                                    onChange={(event) =>
                                                        handleTemplateBodyChange(
                                                            event.target.value
                                                        )
                                                    }
                                                    disabled={
                                                        templateCreationMode ===
                                                            "SUGGESTED" &&
                                                        !selectedGlobalTemplate
                                                    }
                                                />
                                            </Box>
                                            {templateCreationMode ===
                                                "CUSTOM" && (
                                                <Box mt={2}>
                                                    <TextField
                                                        label={
                                                            labels.footerText
                                                        }
                                                        variant="outlined"
                                                        fullWidth
                                                        value={
                                                            customTemplateFooter
                                                        }
                                                        onChange={(event) =>
                                                            setCustomTemplateFooter(
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                    />
                                                </Box>
                                            )}
                                            {templateParameterDefinitions.length >
                                                0 && (
                                                <Box mt={2}>
                                                    <Alert severity="info">
                                                        {labels.parametersHelp}
                                                    </Alert>
                                                    {templateParameterDefinitions.map(
                                                        (definition) => (
                                                            <Grid
                                                                container
                                                                spacing={2}
                                                                key={`${definition.componentType || "body"}-${definition.index}`}
                                                                style={{
                                                                    marginTop: 4
                                                                }}
                                                            >
                                                                {templateCreationMode ===
                                                                    "CUSTOM" && (
                                                                    <Grid
                                                                        item
                                                                        xs={12}
                                                                        sm={4}
                                                                    >
                                                                        <TextField
                                                                            select
                                                                            label={`${labels.parameterSource} {{${definition.index}}}`}
                                                                            variant="outlined"
                                                                            fullWidth
                                                                            value={String(
                                                                                definition.sourceKey ||
                                                                                    ""
                                                                            )}
                                                                            onChange={(
                                                                                event
                                                                            ) =>
                                                                                handleTemplateParameterSourceChange(
                                                                                    Number(
                                                                                        definition.index
                                                                                    ),
                                                                                    String(
                                                                                        definition.componentType ||
                                                                                            "body"
                                                                                    ),
                                                                                    event
                                                                                        .target
                                                                                        .value
                                                                                )
                                                                            }
                                                                        >
                                                                            {getWhatsappTemplateParameterCatalog(
                                                                                customTemplateMessageType
                                                                            ).map(
                                                                                (
                                                                                    item
                                                                                ) => (
                                                                                    <MenuItem
                                                                                        key={
                                                                                            item.key
                                                                                        }
                                                                                        value={
                                                                                            item.key
                                                                                        }
                                                                                    >
                                                                                        {
                                                                                            item.label
                                                                                        }
                                                                                    </MenuItem>
                                                                                )
                                                                            )}
                                                                        </TextField>
                                                                    </Grid>
                                                                )}
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    sm={
                                                                        templateCreationMode ===
                                                                        "CUSTOM"
                                                                            ? 4
                                                                            : 6
                                                                    }
                                                                >
                                                                    <TextField
                                                                        label={`${labels.parameterLabel} {{${definition.index}}}`}
                                                                        variant="outlined"
                                                                        fullWidth
                                                                        value={String(
                                                                            definition.label ||
                                                                                ""
                                                                        )}
                                                                        onChange={(
                                                                            event
                                                                        ) =>
                                                                            handleTemplateParameterDefinitionChange(
                                                                                Number(
                                                                                    definition.index
                                                                                ),
                                                                                "label",
                                                                                event
                                                                                    .target
                                                                                    .value,
                                                                                String(
                                                                                    definition.componentType ||
                                                                                        "body"
                                                                                )
                                                                            )
                                                                        }
                                                                    />
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    sm={
                                                                        templateCreationMode ===
                                                                        "CUSTOM"
                                                                            ? 4
                                                                            : 6
                                                                    }
                                                                >
                                                                    <TextField
                                                                        label={`${labels.parameterExample} {{${definition.index}}}`}
                                                                        variant="outlined"
                                                                        fullWidth
                                                                        value={String(
                                                                            definition.example ||
                                                                                ""
                                                                        )}
                                                                        onChange={(
                                                                            event
                                                                        ) =>
                                                                            handleTemplateParameterDefinitionChange(
                                                                                Number(
                                                                                    definition.index
                                                                                ),
                                                                                "example",
                                                                                event
                                                                                    .target
                                                                                    .value,
                                                                                String(
                                                                                    definition.componentType ||
                                                                                        "body"
                                                                                )
                                                                            )
                                                                        }
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        )
                                                    )}
                                                </Box>
                                            )}
                                            {templateCreationMode ===
                                                "CUSTOM" && (
                                                <Box mt={2}>
                                                    <Typography variant="subtitle2">
                                                        {labels.buttons}
                                                    </Typography>
                                                    {customTemplateButtons.map(
                                                        (button, index) => (
                                                            <Grid
                                                                container
                                                                spacing={2}
                                                                key={index}
                                                                alignItems="center"
                                                                style={{
                                                                    marginTop: 4
                                                                }}
                                                            >
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    sm={3}
                                                                >
                                                                    <TextField
                                                                        select
                                                                        label={
                                                                            labels.buttonType
                                                                        }
                                                                        variant="outlined"
                                                                        fullWidth
                                                                        value={String(
                                                                            button.type ||
                                                                                "QUICK_REPLY"
                                                                        )}
                                                                        onChange={(
                                                                            event
                                                                        ) =>
                                                                            handleCustomTemplateButtonChange(
                                                                                index,
                                                                                "type",
                                                                                event
                                                                                    .target
                                                                                    .value
                                                                            )
                                                                        }
                                                                    >
                                                                        <MenuItem value="QUICK_REPLY">
                                                                            {
                                                                                labels.quickReply
                                                                            }
                                                                        </MenuItem>
                                                                        <MenuItem value="URL">
                                                                            {
                                                                                labels.urlButton
                                                                            }
                                                                        </MenuItem>
                                                                        <MenuItem value="PHONE_NUMBER">
                                                                            {
                                                                                labels.phoneButton
                                                                            }
                                                                        </MenuItem>
                                                                    </TextField>
                                                                </Grid>
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    sm={3}
                                                                >
                                                                    <TextField
                                                                        label={
                                                                            labels.buttonText
                                                                        }
                                                                        variant="outlined"
                                                                        fullWidth
                                                                        value={String(
                                                                            button.text ||
                                                                                ""
                                                                        )}
                                                                        onChange={(
                                                                            event
                                                                        ) =>
                                                                            handleCustomTemplateButtonChange(
                                                                                index,
                                                                                "text",
                                                                                event
                                                                                    .target
                                                                                    .value
                                                                            )
                                                                        }
                                                                    />
                                                                </Grid>
                                                                {button.type ===
                                                                    "URL" && (
                                                                    <Grid
                                                                        item
                                                                        xs={12}
                                                                        sm={5}
                                                                    >
                                                                        <TextField
                                                                            label={
                                                                                labels.buttonUrl
                                                                            }
                                                                            variant="outlined"
                                                                            fullWidth
                                                                            value={String(
                                                                                button.url ||
                                                                                    ""
                                                                            )}
                                                                            onChange={(
                                                                                event
                                                                            ) =>
                                                                                handleCustomTemplateButtonChange(
                                                                                    index,
                                                                                    "url",
                                                                                    event
                                                                                        .target
                                                                                        .value
                                                                                )
                                                                            }
                                                                        />
                                                                    </Grid>
                                                                )}
                                                                {button.type ===
                                                                    "PHONE_NUMBER" && (
                                                                    <Grid
                                                                        item
                                                                        xs={12}
                                                                        sm={5}
                                                                    >
                                                                        <TextField
                                                                            label={
                                                                                labels.buttonPhone
                                                                            }
                                                                            variant="outlined"
                                                                            fullWidth
                                                                            value={String(
                                                                                button.phoneNumber ||
                                                                                    ""
                                                                            )}
                                                                            onChange={(
                                                                                event
                                                                            ) =>
                                                                                handleCustomTemplateButtonChange(
                                                                                    index,
                                                                                    "phoneNumber",
                                                                                    event
                                                                                        .target
                                                                                        .value
                                                                                )
                                                                            }
                                                                        />
                                                                    </Grid>
                                                                )}
                                                                <Grid item>
                                                                    <Tooltip
                                                                        title={
                                                                            labels.removeButton
                                                                        }
                                                                    >
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() =>
                                                                                handleRemoveCustomTemplateButton(
                                                                                    index
                                                                                )
                                                                            }
                                                                            style={createWhatsappActionButtonStyle(
                                                                                "#d32f2f"
                                                                            )}
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Grid>
                                                            </Grid>
                                                        )
                                                    )}
                                                    <Box mt={1}>
                                                        <Button
                                                            color="primary"
                                                            variant="outlined"
                                                            startIcon={
                                                                <AddIcon />
                                                            }
                                                            onClick={
                                                                handleAddCustomTemplateButton
                                                            }
                                                        >
                                                            {labels.addButton}
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            )}
                                            <Box mt={2}>
                                                <Alert severity="info">
                                                    {labels.metaReviewTime}
                                                </Alert>
                                            </Box>
                                            <Box mt={3} mb={2}>
                                                <Divider />
                                            </Box>
                                            <Button
                                                onClick={
                                                    handleCreateWhatsappTemplate
                                                }
                                                color="primary"
                                                variant="contained"
                                                disabled={
                                                    isCreatingWhatsappTemplate ||
                                                    !canSendWhatsappMessages ||
                                                    (templateCreationMode ===
                                                        "SUGGESTED" &&
                                                        !selectedGlobalTemplate)
                                                }
                                            >
                                                {labels.templateCreate}
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} md={5}>
                                            <Typography variant="h6">
                                                {labels.previewTitle}
                                            </Typography>
                                            <Box mt={2}>
                                                <WhatsappTemplatePreview
                                                    bodyText={globalTemplateBody}
                                                    parameterDefinitions={
                                                        templateParameterDefinitions
                                                    }
                                                    headerText={
                                                        templateCreationMode ===
                                                        "CUSTOM"
                                                            ? customTemplateHeader
                                                            : ""
                                                    }
                                                    footerText={
                                                        templateCreationMode ===
                                                        "CUSTOM"
                                                            ? customTemplateFooter
                                                            : ""
                                                    }
                                                    buttons={
                                                        templateCreationMode ===
                                                        "CUSTOM"
                                                            ? customTemplateButtons
                                                            : []
                                                    }
                                                    emptyButtonLabel={labels.buttons}
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {activeTab === 2 && showPreRegistrationSettings && (
                    <WhatsappPreRegistrationSettings
                        companyId={Number(companyId)}
                        featureActive={whatsappPreRegistrationEnabled}
                        canManageFeature={canManagePreRegistrationRollout}
                        changingFeature={isTogglingPreRegistrationRollout}
                        onFeatureActiveChange={updatePreRegistrationRollout}
                    />
                )}

                <Dialog
                    open={!!templateDetails}
                    onClose={() => setTemplateDetails(null)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>{labels.templateDetails}</DialogTitle>
                    <DialogContent>
                        {templateDetails && (
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={7}>
                                    <Typography variant="subtitle2">
                                        {String(
                                            templateDetails.templateName ||
                                                templateDetails.name ||
                                                ""
                                        )}
                                    </Typography>
                                    <Box mt={1}>
                                        {renderStatusChip(
                                            String(templateDetails.status || "")
                                        )}
                                    </Box>
                                    <Box mt={2}>
                                        <Typography variant="body2">
                                            {labels.templateCurrentCategory}:{" "}
                                            {String(
                                                templateDetails.category || ""
                                            )}
                                        </Typography>
                                        <Typography variant="body2">
                                            {labels.templateRequestedCategory}:{" "}
                                            {String(
                                                templateDetails.requestedCategory ||
                                                    ""
                                            )}
                                        </Typography>
                                        <Typography variant="body2">
                                            {labels.quality}:{" "}
                                            {String(
                                                templateDetails.qualityScore ||
                                                    "-"
                                            )}
                                        </Typography>
                                        {templateDetails.statusReason && (
                                            <Typography variant="body2">
                                                {String(
                                                    templateDetails.statusReason
                                                )}
                                            </Typography>
                                        )}
                                        {templateDetails.rejectedReason && (
                                            <Typography variant="body2">
                                                {String(
                                                    templateDetails.rejectedReason
                                                )}
                                            </Typography>
                                        )}
                                    </Box>
                                    {Array.isArray(
                                        templateDetails.parameterDefinitions
                                    ) &&
                                        templateDetails.parameterDefinitions
                                            .length > 0 && (
                                            <Box mt={2}>
                                                <Typography variant="subtitle2">
                                                    {labels.templateParameters}
                                                </Typography>
                                                {templateDetails.parameterDefinitions.map(
                                                    (
                                                        definition: Compleo.IObject
                                                    ) => (
                                                        <Typography
                                                            variant="body2"
                                                            key={String(
                                                                definition.index
                                                            )}
                                                        >
                                                            {`{{${definition.index}}}: ${definition.label} - ${definition.example}`}
                                                        </Typography>
                                                    )
                                                )}
                                            </Box>
                                        )}
                                </Grid>
                                <Grid item xs={12} md={5}>
                                    <WhatsappTemplatePreview
                                        bodyText={getTemplateBodyText(
                                            templateDetails
                                        )}
                                        parameterDefinitions={
                                            getTemplateParameterDefinitionsForPreview(
                                                templateDetails
                                            )
                                        }
                                        emptyButtonLabel={labels.buttons}
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setTemplateDetails(null)}>
                            {labels.close}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Page>
    );
};

export default WhatsappSettings;
